import { AfterViewInit, Component, HostBinding, NgZone, OnDestroy, OnInit } from '@angular/core';
import { COLOR_PICKER_MORE_TOOL } from '../../../../data/product/color-pickers.data';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subscription } from 'rxjs';
import { ColorService } from 'src/app/service/color/color.service';
import { SeoService } from 'src/app/service/seo/seo.service';
import { BLOG } from 'src/app/data/blog/blog.data';
import { converterFaq } from 'src/app/data/tool/tool-about.data';
import { URLS } from 'src/app/data/navigation/navigation.data';
import { THEMES } from 'src/app/data/product/product.data';
import { Product } from 'src/app/model/product/product.model';

// One exact-match conversion page (e.g. "HEX to RGB"). It reuses the color
// tool's hero (app-color-widget) on the same color-derived backdrop, so it looks
// and behaves exactly like the color page; the config only labels the direction
// and supplies the worked examples. Each direction gets its own URL + config.
interface ConverterExample {
  name: string;
  hex: string;
  rgb: string;
  hsl: string;
  hsv: string;
  hwb: string;
  cmyk: string;
  oklch: string;
  oklab: string;
  cielab: string;
  cielch: string;
  p3: string;
  xyz: string;
  yxy: string;
  hunter: string;
  cieluv: string;
}

interface ConverterDef {
  slug: string;
  family: 'rgb' | 'hsl' | 'hsv' | 'hsb' | 'hwb' | 'cmyk' | 'oklch' | 'oklab' | 'lab' | 'lch' | 'p3' | 'xyz' | 'yxy' | 'hunter' | 'luv';
  // The family's spelled-out components, for the how-it-works copy.
  components: string;
  fromLabel: string;
  toLabel: string;
  heading: string;
  sample: string; // a full 6-digit hex, e.g. '#549EFF'
  examples: ConverterExample[];
}

// The exact-match converters that exist today; the "Other Converters" section
// lists only these (a new direction is added here when it ships). Every
// direction has its own icon (predefined-gradient badge) so the section
// renders the same tool cards as the /tools page.
interface ConverterListItem {
  slug: string;
  label: string;
  desc: string;
  logo: string;
  built: boolean;
}

const CONVERTER_LIST: ConverterListItem[] = [
  { slug: 'hex-to-rgb', label: 'HEX to RGB', desc: 'Convert any hex color code to its rgb() value instantly', logo: './assets/product-lines/tools/hex-to-rgb.svg', built: true },
  { slug: 'rgb-to-hex', label: 'RGB to HEX', desc: 'Convert RGB color values to their hex codes instantly', logo: './assets/product-lines/tools/rgb-to-hex.svg', built: true },
  { slug: 'hex-to-hsl', label: 'HEX to HSL', desc: 'Convert hex color codes to hue, saturation and lightness', logo: './assets/product-lines/tools/hex-to-hsl.svg', built: true },
  { slug: 'hsl-to-hex', label: 'HSL to HEX', desc: 'Convert hue, saturation and lightness to hex color codes', logo: './assets/product-lines/tools/hsl-to-hex.svg', built: true },
  { slug: 'hex-to-hsv', label: 'HEX to HSV', desc: 'Convert hex color codes to hue, saturation and value', logo: './assets/product-lines/tools/hex-to-hsv.svg', built: true },
  { slug: 'hsv-to-hex', label: 'HSV to HEX', desc: 'Convert hue, saturation and value to hex color codes', logo: './assets/product-lines/tools/hsv-to-hex.svg', built: true },
  { slug: 'hex-to-hsb', label: 'HEX to HSB', desc: 'Convert hex color codes to hue, saturation and brightness', logo: './assets/product-lines/tools/hex-to-hsb.svg', built: true },
  { slug: 'hsb-to-hex', label: 'HSB to HEX', desc: 'Convert hue, saturation and brightness to hex color codes', logo: './assets/product-lines/tools/hsb-to-hex.svg', built: true },
  { slug: 'hex-to-hwb', label: 'HEX to HWB', desc: 'Convert hex color codes to hue, whiteness and blackness', logo: './assets/product-lines/tools/hex-to-hwb.svg', built: true },
  { slug: 'hwb-to-hex', label: 'HWB to HEX', desc: 'Convert hue, whiteness and blackness to hex color codes', logo: './assets/product-lines/tools/hwb-to-hex.svg', built: true },
  { slug: 'hex-to-cmyk', label: 'HEX to CMYK', desc: 'Convert hex color codes to cyan, magenta, yellow and key', logo: './assets/product-lines/tools/hex-to-cmyk.svg', built: true },
  { slug: 'cmyk-to-hex', label: 'CMYK to HEX', desc: 'Convert cyan, magenta, yellow and key to hex color codes', logo: './assets/product-lines/tools/cmyk-to-hex.svg', built: true },
  { slug: 'hex-to-oklab', label: 'HEX to OKLAB', desc: 'Convert hex color codes to OKLAB lightness and a/b axes', logo: './assets/product-lines/tools/hex-to-oklab.svg', built: true },
  { slug: 'oklab-to-hex', label: 'OKLAB to HEX', desc: 'Convert OKLAB lightness and a/b axes to hex color codes', logo: './assets/product-lines/tools/oklab-to-hex.svg', built: true },
  { slug: 'hex-to-oklch', label: 'HEX to OKLCH', desc: 'Convert hex color codes to OKLCH lightness, chroma and hue', logo: './assets/product-lines/tools/hex-to-oklch.svg', built: true },
  { slug: 'oklch-to-hex', label: 'OKLCH to HEX', desc: 'Convert OKLCH lightness, chroma and hue to hex color codes', logo: './assets/product-lines/tools/oklch-to-hex.svg', built: true },
  { slug: 'hex-to-cielab', label: 'HEX to CIELAB', desc: 'Convert hex color codes to CIELAB lightness and a/b axes', logo: './assets/product-lines/tools/hex-to-cielab.svg', built: true },
  { slug: 'cielab-to-hex', label: 'CIELAB to HEX', desc: 'Convert CIELAB lightness and a/b axes to hex color codes', logo: './assets/product-lines/tools/cielab-to-hex.svg', built: true },
  { slug: 'hex-to-cielch', label: 'HEX to CIELCH', desc: 'Convert hex color codes to CIELCH lightness, chroma and hue', logo: './assets/product-lines/tools/hex-to-cielch.svg', built: true },
  { slug: 'cielch-to-hex', label: 'CIELCH to HEX', desc: 'Convert CIELCH lightness, chroma and hue to hex color codes', logo: './assets/product-lines/tools/cielch-to-hex.svg', built: true },
  { slug: 'hex-to-cieluv', label: 'HEX to CIELUV', desc: 'Convert hex color codes to CIELUV lightness and u/v axes', logo: './assets/product-lines/tools/hex-to-cieluv.svg?v=20261011', built: true },
  { slug: 'cieluv-to-hex', label: 'CIELUV to HEX', desc: 'Convert CIELUV lightness and u/v axes to hex color codes', logo: './assets/product-lines/tools/cieluv-to-hex.svg?v=20261011', built: true },
  { slug: 'hex-to-display-p3', label: 'HEX to Display P3', desc: 'Convert hex color codes to Display P3 red, green and blue', logo: './assets/product-lines/tools/hex-to-display-p3.svg?v=20260917', built: true },
  { slug: 'display-p3-to-hex', label: 'Display P3 to HEX', desc: 'Convert Display P3 red, green and blue to hex color codes', logo: './assets/product-lines/tools/display-p3-to-hex.svg?v=20260917', built: true },
  { slug: 'hex-to-xyz', label: 'HEX to XYZ', desc: 'Convert hex color codes to CIE XYZ tristimulus values', logo: './assets/product-lines/tools/hex-to-xyz.svg?v=20260917', built: true },
  { slug: 'xyz-to-hex', label: 'XYZ to HEX', desc: 'Convert CIE XYZ tristimulus values to hex color codes', logo: './assets/product-lines/tools/xyz-to-hex.svg?v=20260917', built: true },
  { slug: 'hex-to-yxy', label: 'HEX to Yxy', desc: 'Convert hex color codes to CIE Yxy luminance and chromaticity', logo: './assets/product-lines/tools/hex-to-yxy.svg?v=20260917', built: true },
  { slug: 'yxy-to-hex', label: 'Yxy to HEX', desc: 'Convert CIE Yxy luminance and chromaticity to hex color codes', logo: './assets/product-lines/tools/yxy-to-hex.svg?v=20260917', built: true },
  { slug: 'hex-to-hunter-lab', label: 'HEX to Hunter Lab', desc: 'Convert hex color codes to Hunter Lab lightness and a/b axes', logo: './assets/product-lines/tools/hex-to-hunter-lab.svg?v=20260917', built: true },
  { slug: 'hunter-lab-to-hex', label: 'Hunter Lab to HEX', desc: 'Convert Hunter Lab lightness and a/b axes to hex color codes', logo: './assets/product-lines/tools/hunter-lab-to-hex.svg?v=20260917', built: true },
];

const CONVERTERS: { [ slug: string ]: ConverterDef } = {
  'hex-to-rgb': {
    slug: 'hex-to-rgb',
    family: 'rgb',
    components: 'red, green and blue',
    fromLabel: 'HEX',
    toLabel: 'RGB',
    heading: 'HEX to RGB Converter',
    sample: '#549EFF',
    examples: [
      { name: 'White', hex: '#FFFFFF', rgb: 'rgb(255, 255, 255)', hsl: 'hsl(0, 0%, 100%)', hsv: 'hsv(0, 0%, 100%)', hwb: 'hwb(0 100% 0%)', cmyk: 'cmyk(0%, 0%, 0%, 0%)', oklch: 'oklch(100% 0 0)', oklab: 'oklab(100% 0 0)', cielab: 'lab(100% 0 0)', cielch: 'lch(100% 0 158)', p3: 'color(display-p3 1 1 1)', xyz: 'xyz(95.05, 100, 108.88)', yxy: 'yxy(100, 0.3127, 0.329)', hunter: 'hunter-lab(100, -5.34, 5.44)', cieluv: 'luv(100, 0, 0)' },
      { name: 'Black', hex: '#000000', rgb: 'rgb(0, 0, 0)', hsl: 'hsl(0, 0%, 0%)', hsv: 'hsv(0, 0%, 0%)', hwb: 'hwb(0 0% 100%)', cmyk: 'cmyk(0%, 0%, 0%, 100%)', oklch: 'oklch(0% 0 0)', oklab: 'oklab(0% 0 0)', cielab: 'lab(0% 0 0)', cielch: 'lch(0% 0 0)', p3: 'color(display-p3 0 0 0)', xyz: 'xyz(0, 0, 0)', yxy: 'yxy(0, 0, 0)', hunter: 'hunter-lab(0, 0, 0)', cieluv: 'luv(0, 0, 0)' },
      { name: 'Red', hex: '#FF0000', rgb: 'rgb(255, 0, 0)', hsl: 'hsl(0, 100%, 50%)', hsv: 'hsv(0, 100%, 100%)', hwb: 'hwb(0 0% 0%)', cmyk: 'cmyk(0%, 100%, 100%, 0%)', oklch: 'oklch(63% 0.258 29)', oklab: 'oklab(63% 0.225 0.126)', cielab: 'lab(53% 80.09 67.2)', cielch: 'lch(53% 104.55 40)', p3: 'color(display-p3 0.9175 0.2003 0.1386)', xyz: 'xyz(41.25, 21.27, 1.93)', yxy: 'yxy(21.27, 0.64, 0.33)', hunter: 'hunter-lab(46.12, 78.94, 29.8)', cieluv: 'luv(53.24, 175.02, 37.76)' },
      { name: 'Lime', hex: '#00FF00', rgb: 'rgb(0, 255, 0)', hsl: 'hsl(120, 100%, 50%)', hsv: 'hsv(120, 100%, 100%)', hwb: 'hwb(120 0% 0%)', cmyk: 'cmyk(100%, 0%, 100%, 0%)', oklch: 'oklch(87% 0.295 143)', oklab: 'oklab(87% -0.234 0.179)', cielab: 'lab(88% -86.18 83.18)', cielch: 'lch(88% 119.78 136)', p3: 'color(display-p3 0.4584 0.9853 0.2983)', xyz: 'xyz(35.76, 71.52, 11.92)', yxy: 'yxy(71.52, 0.3, 0.6)', hunter: 'hunter-lab(84.57, -72.52, 50.84)', cieluv: 'luv(87.73, -83.08, 107.4)' },
      { name: 'Blue', hex: '#0000FF', rgb: 'rgb(0, 0, 255)', hsl: 'hsl(240, 100%, 50%)', hsv: 'hsv(240, 100%, 100%)', hwb: 'hwb(240 0% 0%)', cmyk: 'cmyk(100%, 100%, 0%, 0%)', oklch: 'oklch(45% 0.314 264)', oklab: 'oklab(45% -0.032 -0.312)', cielab: 'lab(32% 79.19 -107.86)', cielch: 'lch(32% 133.81 306)', p3: 'color(display-p3 0 0 0.9596)', xyz: 'xyz(18.04, 7.22, 95.03)', yxy: 'yxy(7.22, 0.15, 0.06)', hunter: 'hunter-lab(26.87, 72.87, -190.92)', cieluv: 'luv(32.3, -9.41, -130.34)' },
      { name: 'Yellow', hex: '#FFFF00', rgb: 'rgb(255, 255, 0)', hsl: 'hsl(60, 100%, 50%)', hsv: 'hsv(60, 100%, 100%)', hwb: 'hwb(60 0% 0%)', cmyk: 'cmyk(0%, 0%, 100%, 0%)', oklch: 'oklch(97% 0.211 110)', oklab: 'oklab(97% -0.071 0.199)', cielab: 'lab(97% -21.55 94.48)', cielch: 'lch(97% 96.91 103)', p3: 'color(display-p3 1 1 0.3309)', xyz: 'xyz(77, 92.78, 13.85)', yxy: 'yxy(92.78, 0.4193, 0.5052)', hunter: 'hunter-lab(96.32, -25.87, 58.9)', cieluv: 'luv(97.14, 7.71, 106.79)' },
      { name: 'Cyan', hex: '#00FFFF', rgb: 'rgb(0, 255, 255)', hsl: 'hsl(180, 100%, 50%)', hsv: 'hsv(180, 100%, 100%)', hwb: 'hwb(180 0% 0%)', cmyk: 'cmyk(100%, 0%, 0%, 0%)', oklch: 'oklch(91% 0.154 195)', oklab: 'oklab(91% -0.149 -0.039)', cielab: 'lab(91% -48.09 -14.13)', cielch: 'lch(91% 50.12 196)', p3: 'color(display-p3 0.4584 0.9853 0.9925)', xyz: 'xyz(53.8, 78.73, 106.95)', yxy: 'yxy(78.73, 0.2247, 0.3288)', hunter: 'hunter-lab(88.73, -47.05, -9.35)', cieluv: 'luv(91.11, -70.48, -15.2)' },
      { name: 'Magenta', hex: '#FF00FF', rgb: 'rgb(255, 0, 255)', hsl: 'hsl(300, 100%, 50%)', hsv: 'hsv(300, 100%, 100%)', hwb: 'hwb(300 0% 0%)', cmyk: 'cmyk(0%, 100%, 0%, 0%)', oklch: 'oklch(70% 0.323 328)', oklab: 'oklab(70% 0.275 -0.169)', cielab: 'lab(60% 98.23 -60.82)', cielch: 'lch(60% 115.54 328)', p3: 'color(display-p3 0.9175 0.2003 0.9675)', xyz: 'xyz(59.29, 28.48, 96.96)', yxy: 'yxy(28.48, 0.3209, 0.1542)', hunter: 'hunter-lab(53.37, 104.89, -70.36)', cieluv: 'luv(60.32, 84.07, -108.68)' },
      { name: 'Silver', hex: '#C0C0C0', rgb: 'rgb(192, 192, 192)', hsl: 'hsl(0, 0%, 75%)', hsv: 'hsv(0, 0%, 75%)', hwb: 'hwb(0 75% 25%)', cmyk: 'cmyk(0%, 0%, 0%, 25%)', oklch: 'oklch(81% 0 0)', oklab: 'oklab(81% 0 0)', cielab: 'lab(78% 0 0)', cielch: 'lch(78% 0 158)', p3: 'color(display-p3 0.7529 0.7529 0.7529)', xyz: 'xyz(50.1, 52.71, 57.39)', yxy: 'yxy(52.71, 0.3127, 0.329)', hunter: 'hunter-lab(72.6, -3.88, 3.95)', cieluv: 'luv(77.7, 0, 0)' },
      { name: 'Gray', hex: '#808080', rgb: 'rgb(128, 128, 128)', hsl: 'hsl(0, 0%, 50%)', hsv: 'hsv(0, 0%, 50%)', hwb: 'hwb(0 50% 50%)', cmyk: 'cmyk(0%, 0%, 0%, 50%)', oklch: 'oklch(60% 0 0)', oklab: 'oklab(60% 0 0)', cielab: 'lab(54% 0 0)', cielch: 'lch(54% 0 158)', p3: 'color(display-p3 0.502 0.502 0.502)', xyz: 'xyz(20.52, 21.59, 23.5)', yxy: 'yxy(21.59, 0.3127, 0.329)', hunter: 'hunter-lab(46.46, -2.48, 2.53)', cieluv: 'luv(53.59, 0, 0)' },
      { name: 'Maroon', hex: '#800000', rgb: 'rgb(128, 0, 0)', hsl: 'hsl(0, 100%, 25%)', hsv: 'hsv(0, 100%, 50%)', hwb: 'hwb(0 0% 50%)', cmyk: 'cmyk(0%, 100%, 100%, 50%)', oklch: 'oklch(38% 0.154 29)', oklab: 'oklab(38% 0.135 0.075)', cielab: 'lab(26% 48.05 38.06)', cielch: 'lch(26% 61.29 38)', p3: 'color(display-p3 0.4584 0.0798 0.0472)', xyz: 'xyz(8.9, 4.59, 0.42)', yxy: 'yxy(4.59, 0.64, 0.33)', hunter: 'hunter-lab(21.43, 36.68, 13.84)', cieluv: 'luv(25.54, 83.94, 18.11)' },
      { name: 'Olive', hex: '#808000', rgb: 'rgb(128, 128, 0)', hsl: 'hsl(60, 100%, 25%)', hsv: 'hsv(60, 100%, 50%)', hwb: 'hwb(60 0% 50%)', cmyk: 'cmyk(0%, 0%, 100%, 50%)', oklch: 'oklch(58% 0.127 110)', oklab: 'oklab(58% -0.043 0.119)', cielab: 'lab(52% -12.93 56.67)', cielch: 'lch(52% 58.13 103)', p3: 'color(display-p3 0.502 0.502 0.1487)', xyz: 'xyz(16.62, 20.03, 2.99)', yxy: 'yxy(20.03, 0.4193, 0.5052)', hunter: 'hunter-lab(44.75, -12.02, 27.37)', cieluv: 'luv(51.87, 4.11, 57.02)' },
      { name: 'Green', hex: '#008000', rgb: 'rgb(0, 128, 0)', hsl: 'hsl(120, 100%, 25%)', hsv: 'hsv(120, 100%, 50%)', hwb: 'hwb(120 0% 50%)', cmyk: 'cmyk(100%, 0%, 100%, 50%)', oklch: 'oklch(52% 0.177 142)', oklab: 'oklab(52% -0.14 0.108)', cielab: 'lab(46% -51.7 49.9)', cielch: 'lch(46% 71.85 136)', p3: 'color(display-p3 0.216 0.4942 0.1315)', xyz: 'xyz(7.72, 15.44, 2.57)', yxy: 'yxy(15.44, 0.3, 0.6)', hunter: 'hunter-lab(39.29, -33.69, 23.62)', cieluv: 'luv(46.23, -43.77, 56.59)' },
      { name: 'Purple', hex: '#800080', rgb: 'rgb(128, 0, 128)', hsl: 'hsl(300, 100%, 25%)', hsv: 'hsv(300, 100%, 50%)', hwb: 'hwb(300 0% 50%)', cmyk: 'cmyk(0%, 100%, 0%, 50%)', oklch: 'oklch(42% 0.193 329)', oklab: 'oklab(42% 0.165 -0.101)', cielab: 'lab(30% 58.93 -36.49)', cielch: 'lch(30% 69.31 328)', p3: 'color(display-p3 0.4584 0.0798 0.4848)', xyz: 'xyz(12.8, 6.15, 20.93)', yxy: 'yxy(6.15, 0.3209, 0.1542)', hunter: 'hunter-lab(24.8, 48.73, -32.69)', cieluv: 'luv(29.78, 41.51, -53.66)' },
      { name: 'Teal', hex: '#008080', rgb: 'rgb(0, 128, 128)', hsl: 'hsl(180, 100%, 25%)', hsv: 'hsv(180, 100%, 50%)', hwb: 'hwb(180 0% 50%)', cmyk: 'cmyk(100%, 0%, 0%, 50%)', oklch: 'oklch(54% 0.093 195)', oklab: 'oklab(54% -0.09 -0.024)', cielab: 'lab(48% -28.85 -8.48)', cielch: 'lch(48% 30.07 196)', p3: 'color(display-p3 0.216 0.4942 0.498)', xyz: 'xyz(11.61, 17, 23.09)', yxy: 'yxy(17, 0.2247, 0.3288)', hunter: 'hunter-lab(41.23, -21.86, -4.34)', cieluv: 'luv(48.25, -37.33, -8.05)' },
      { name: 'Navy', hex: '#000080', rgb: 'rgb(0, 0, 128)', hsl: 'hsl(240, 100%, 25%)', hsv: 'hsv(240, 100%, 50%)', hwb: 'hwb(240 0% 50%)', cmyk: 'cmyk(100%, 100%, 0%, 50%)', oklch: 'oklch(27% 0.188 264)', oklab: 'oklab(27% -0.019 -0.187)', cielab: 'lab(13% 47.5 -64.7)', cielch: 'lch(13% 80.27 306)', p3: 'color(display-p3 0 0 0.4806)', xyz: 'xyz(3.89, 1.56, 20.51)', yxy: 'yxy(1.56, 0.15, 0.06)', hunter: 'hunter-lab(12.48, 33.86, -88.7)', cieluv: 'luv(12.97, -3.78, -52.35)' },
      { name: 'Orange', hex: '#FFA500', rgb: 'rgb(255, 165, 0)', hsl: 'hsl(39, 100%, 50%)', hsv: 'hsv(39, 100%, 100%)', hwb: 'hwb(39 0% 0%)', cmyk: 'cmyk(0%, 35%, 100%, 0%)', oklch: 'oklch(79% 0.171 71)', oklab: 'oklab(79% 0.057 0.161)', cielab: 'lab(75% 23.93 78.95)', cielch: 'lch(75% 82.5 73)', p3: 'color(display-p3 0.9497 0.6629 0.233)', xyz: 'xyz(54.7, 48.18, 6.42)', yxy: 'yxy(48.18, 0.5005, 0.4408)', hunter: 'hunter-lab(69.41, 19.21, 43.1)', cieluv: 'luv(74.94, 74.85, 74)' },
      { name: 'Gold', hex: '#FFD700', rgb: 'rgb(255, 215, 0)', hsl: 'hsl(51, 100%, 50%)', hsv: 'hsv(51, 100%, 100%)', hwb: 'hwb(51 0% 0%)', cmyk: 'cmyk(0%, 16%, 100%, 0%)', oklch: 'oklch(89% 0.182 95)', oklab: 'oklab(89% -0.017 0.181)', cielab: 'lab(87% -1.92 87.13)', cielch: 'lch(87% 87.15 91)', p3: 'color(display-p3 0.9746 0.849 0.2855)', xyz: 'xyz(65.54, 69.86, 10.03)', yxy: 'yxy(69.86, 0.4507, 0.4804)', hunter: 'hunter-lab(83.59, -6.3, 51.39)', cieluv: 'luv(86.93, 35.5, 92.09)' },
      { name: 'Pink', hex: '#FFC0CB', rgb: 'rgb(255, 192, 203)', hsl: 'hsl(350, 100%, 88%)', hsv: 'hsv(350, 25%, 100%)', hwb: 'hwb(350 75% 0%)', cmyk: 'cmyk(0%, 25%, 20%, 0%)', oklch: 'oklch(87% 0.074 7)', oklab: 'oklab(87% 0.073 0.009)', cielab: 'lab(84% 24.14 3.33)', cielch: 'lch(84% 24.37 8)', p3: 'color(display-p3 0.9621 0.7629 0.7972)', xyz: 'xyz(70.87, 63.27, 64.97)', yxy: 'yxy(63.27, 0.3559, 0.3178)', hunter: 'hunter-lab(79.55, 19.83, 7.26)', cieluv: 'luv(83.59, 38.57, 0.44)' },
      { name: 'Coral', hex: '#FF7F50', rgb: 'rgb(255, 127, 80)', hsl: 'hsl(16, 100%, 66%)', hsv: 'hsv(16, 69%, 100%)', hwb: 'hwb(16 31% 0%)', cmyk: 'cmyk(0%, 50%, 69%, 0%)', oklch: 'oklch(74% 0.168 40)', oklab: 'oklab(74% 0.128 0.109)', cielab: 'lab(67% 45.35 47.49)', cielch: 'lch(67% 65.67 46)', p3: 'color(display-p3 0.9358 0.5255 0.3583)', xyz: 'xyz(50.28, 37.02, 12.09)', yxy: 'yxy(37.02, 0.5059, 0.3725)', hunter: 'hunter-lab(60.85, 41.02, 30.82)', cieluv: 'luv(67.3, 101.04, 44.42)' },
    ],
  },
};

// RGB -> HEX is the exact mirror of HEX -> RGB: same page, same sections, same
// example colors, with the direction (and the worked math) reversed.
CONVERTERS[ 'rgb-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'rgb-to-hex',
  fromLabel: 'RGB',
  toLabel: 'HEX',
  heading: 'RGB to HEX Converter',
};

// The HSL family mirrors the RGB one exactly: same page, same sections, same
// example colors, with HSL as the other notation.
CONVERTERS[ 'hex-to-hsl' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-hsl',
  family: 'hsl',
  components: 'hue, saturation and lightness',
  fromLabel: 'HEX',
  toLabel: 'HSL',
  heading: 'HEX to HSL Converter',
};

CONVERTERS[ 'hsl-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-hsl' ],
  slug: 'hsl-to-hex',
  fromLabel: 'HSL',
  toLabel: 'HEX',
  heading: 'HSL to HEX Converter',
};

// And the HSV (a.k.a. HSB) family, same page again with value instead of
// lightness.
CONVERTERS[ 'hex-to-hsv' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-hsv',
  family: 'hsv',
  components: 'hue, saturation and value',
  fromLabel: 'HEX',
  toLabel: 'HSV',
  heading: 'HEX to HSV Converter',
};

CONVERTERS[ 'hsv-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-hsv' ],
  slug: 'hsv-to-hex',
  fromLabel: 'HSV',
  toLabel: 'HEX',
  heading: 'HSV to HEX Converter',
};

// HSB is HSV under its designer-tool name (Photoshop / Sketch call value
// "brightness"): the same math and the same numbers, written hsb(...).
CONVERTERS[ 'hex-to-hsb' ] = {
  ...CONVERTERS[ 'hex-to-hsv' ],
  slug: 'hex-to-hsb',
  family: 'hsb',
  components: 'hue, saturation and brightness',
  fromLabel: 'HEX',
  toLabel: 'HSB',
  heading: 'HEX to HSB Converter',
};

CONVERTERS[ 'hsb-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-hsb' ],
  slug: 'hsb-to-hex',
  fromLabel: 'HSB',
  toLabel: 'HEX',
  heading: 'HSB to HEX Converter',
};

// HWB (CSS Color 4) describes a hue mixed with white and black: whiteness
// lifts every channel's floor, blackness lowers the ceiling.
CONVERTERS[ 'hex-to-hwb' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-hwb',
  family: 'hwb',
  components: 'hue, whiteness and blackness',
  fromLabel: 'HEX',
  toLabel: 'HWB',
  heading: 'HEX to HWB Converter',
};

CONVERTERS[ 'hwb-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-hwb' ],
  slug: 'hwb-to-hex',
  fromLabel: 'HWB',
  toLabel: 'HEX',
  heading: 'HWB to HEX Converter',
};

// CMYK is the print family and the only four channel one: ink coverages for
// cyan, magenta, yellow and the shared key (black) plate.
CONVERTERS[ 'hex-to-cmyk' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-cmyk',
  family: 'cmyk',
  components: 'cyan, magenta, yellow and key',
  fromLabel: 'HEX',
  toLabel: 'CMYK',
  heading: 'HEX to CMYK Converter',
};

CONVERTERS[ 'cmyk-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-cmyk' ],
  slug: 'cmyk-to-hex',
  fromLabel: 'CMYK',
  toLabel: 'HEX',
  heading: 'CMYK to HEX Converter',
};

// OKLCH (CSS Color 4) is the perceptual family: a lightness, a chroma (how
// colorful) and a hue angle, computed through OKLAB so equal steps look equal.
CONVERTERS[ 'hex-to-oklch' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-oklch',
  family: 'oklch',
  components: 'lightness, chroma and hue',
  fromLabel: 'HEX',
  toLabel: 'OKLCH',
  heading: 'HEX to OKLCH Converter',
};

CONVERTERS[ 'oklch-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-oklch' ],
  slug: 'oklch-to-hex',
  fromLabel: 'OKLCH',
  toLabel: 'HEX',
  heading: 'OKLCH to HEX Converter',
};

// OKLAB is the Cartesian sibling of OKLCH: the same perceptual space, but a
// lightness with two signed axes - a (green to red) and b (blue to yellow) -
// instead of a chroma and hue angle.
CONVERTERS[ 'hex-to-oklab' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-oklab',
  family: 'oklab',
  components: 'lightness and the a and b axes',
  fromLabel: 'HEX',
  toLabel: 'OKLAB',
  heading: 'HEX to OKLAB Converter',
};

CONVERTERS[ 'oklab-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-oklab' ],
  slug: 'oklab-to-hex',
  fromLabel: 'OKLAB',
  toLabel: 'HEX',
  heading: 'OKLAB to HEX Converter',
};

// CIELAB (CIE 1976 L*a*b*) is the classic perceptual space: a lightness with two
// signed axes - a (green to red) and b (blue to yellow) - like OKLab but on the
// older CIE model, computed through XYZ. Its CSS function is lab().
CONVERTERS[ 'hex-to-cielab' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-cielab',
  family: 'lab',
  components: 'lightness and the a and b axes',
  fromLabel: 'HEX',
  toLabel: 'CIELAB',
  heading: 'HEX to CIELAB Converter',
};

CONVERTERS[ 'cielab-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-cielab' ],
  slug: 'cielab-to-hex',
  fromLabel: 'CIELAB',
  toLabel: 'HEX',
  heading: 'CIELAB to HEX Converter',
};

// CIELCH is the cylindrical form of CIELAB: the same space, but a lightness,
// a chroma (how colorful) and a hue angle instead of the a/b axes. CSS: lch().
CONVERTERS[ 'hex-to-cielch' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-cielch',
  family: 'lch',
  components: 'lightness, chroma and hue',
  fromLabel: 'HEX',
  toLabel: 'CIELCH',
  heading: 'HEX to CIELCH Converter',
};

CONVERTERS[ 'cielch-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-cielch' ],
  slug: 'cielch-to-hex',
  fromLabel: 'CIELCH',
  toLabel: 'HEX',
  heading: 'CIELCH to HEX Converter',
};

// CIELUV (CIE 1976 L*u*v*) is the CIELAB sibling built for additive light: the
// same lightness, with u and v measured from the white point on the CIE 1976
// u′v′ chromaticity diagram. CSS has no luv(), so values are written luv(L, u, v).
CONVERTERS[ 'hex-to-cieluv' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-cieluv',
  family: 'luv',
  components: 'lightness and the u/v axes',
  fromLabel: 'HEX',
  toLabel: 'CIELUV',
  heading: 'HEX to CIELUV Converter',
};

CONVERTERS[ 'cieluv-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-cieluv' ],
  slug: 'cieluv-to-hex',
  fromLabel: 'CIELUV',
  toLabel: 'HEX',
  heading: 'CIELUV to HEX Converter',
};

// Display P3 is the wide-gamut RGB of modern screens: the same red, green and
// blue idea as sRGB, but with more saturated primaries. CSS: color(display-p3).
CONVERTERS[ 'hex-to-display-p3' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-display-p3',
  family: 'p3',
  components: 'red, green and blue',
  fromLabel: 'HEX',
  toLabel: 'Display P3',
  heading: 'HEX to Display P3 Converter',
};

CONVERTERS[ 'display-p3-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-display-p3' ],
  slug: 'display-p3-to-hex',
  fromLabel: 'Display P3',
  toLabel: 'HEX',
  heading: 'Display P3 to HEX Converter',
};

// CIE XYZ is the device-independent root of every perceptual space: three
// tristimulus values (D65 white at Y = 100) that CIELAB, OKLAB and the rest
// are all derived from. Shown on the 0..100 scale.
CONVERTERS[ 'hex-to-xyz' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-xyz',
  family: 'xyz',
  components: 'X, Y and Z tristimulus values',
  fromLabel: 'HEX',
  toLabel: 'XYZ',
  heading: 'HEX to XYZ Converter',
};

CONVERTERS[ 'xyz-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-xyz' ],
  slug: 'xyz-to-hex',
  fromLabel: 'XYZ',
  toLabel: 'HEX',
  heading: 'XYZ to HEX Converter',
};

// CIE Yxy (xyY) separates XYZ into luminance Y and the chromaticity
// coordinates x, y of the CIE 1931 diagram.
CONVERTERS[ 'hex-to-yxy' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-yxy',
  family: 'yxy',
  components: 'luminance Y and chromaticity x, y',
  fromLabel: 'HEX',
  toLabel: 'Yxy',
  heading: 'HEX to Yxy Converter',
};

CONVERTERS[ 'yxy-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-yxy' ],
  slug: 'yxy-to-hex',
  fromLabel: 'Yxy',
  toLabel: 'HEX',
  heading: 'Yxy to HEX Converter',
};

// Hunter Lab (1958) is the older opponent-color space that CIELAB refined:
// L from the square root of Y, a and b from the X/Y and Y/Z differences.
CONVERTERS[ 'hex-to-hunter-lab' ] = {
  ...CONVERTERS[ 'hex-to-rgb' ],
  slug: 'hex-to-hunter-lab',
  family: 'hunter',
  components: 'lightness and the a/b axes',
  fromLabel: 'HEX',
  toLabel: 'Hunter Lab',
  heading: 'HEX to Hunter Lab Converter',
};

CONVERTERS[ 'hunter-lab-to-hex' ] = {
  ...CONVERTERS[ 'hex-to-hunter-lab' ],
  slug: 'hunter-lab-to-hex',
  fromLabel: 'Hunter Lab',
  toLabel: 'HEX',
  heading: 'Hunter Lab to HEX Converter',
};

@Component( {
  selector: 'app-converter-page',
  templateUrl: './converter-page.component.html',
  styleUrls: [ './converter-page.component.scss', '../tools-page/tools-page.component.scss', '../../plugin-pages/plugins-page/plugins-page.component.scss' ],
} )
export class ConverterPageComponent implements OnInit, AfterViewInit, OnDestroy {
  def!: ConverterDef;
  isReverse = false; // true on rgb-to-hex: same page with the direction mirrored
  URLS = URLS;
  hex = '#549EFF';
  heroBackground = '';
  dataReady = false;
  /** More Color Tools: the Color Picker */
  readonly colorPicker = COLOR_PICKER_MORE_TOOL;
  readonly moreToolsSource = 'converter';
  activeSection = 'top';
  pageSections: { key: string; label: string }[] = [];
  private sectionObserver?: IntersectionObserver;

  otherConverters: ConverterListItem[] = [];
  relatedThemeList: { product: Product; category: string }[] = [];
  blogCards = [ ...BLOG ].sort( () => Math.random() - 0.5 ).slice( 0, 3 );
  /** the four questions closing the page, written for this direction (the server prints the same as FAQPage) */
  faq: { q: string; a: string }[] = [];

  // Extra output formats for the current color (RGB written every common way),
  // and the worked hex->decimal breakdown for the "how it works" section.
  rgbFormats: { label: string; value: string; key: string }[] = [];
  workedChannels: { channel: string; hexPair: string; hi: number; lo: number; dec: number; tint: string }[] = [];
  rgbString = '';
  // The current family's non-hex notation string (hsl(...) or hsv(...)).
  otherString = '';
  // The source split for the X-to-hex "how it works" visual.
  otherParts: { display: string; cap: string; tint: string }[] = [];
  // The worked derivation steps for the HSL / HSV families: every intermediate
  // value (max/min/chroma, then how each component is found) with real numbers.
  otherSteps: { label: string; expr: string }[] = [];
  outHex = '';
  copiedKey = '';
  private copiedTimer: any;

  // The sidebar's "On This Page" color entry shows the SOURCE value of the
  // conversion: the notation on reverse pages (cmyk(...), hsl(...)), the hex on
  // forward pages.
  get sourceLabel(): string {
    return this.isReverse ? this.otherString : this.hex.toUpperCase();
  }

  /** the conversion's result, under the hero title ("#549EFF is rgb(84, 158, 255)", owner 2026-10-12) */
  get targetLabel(): string {
    return this.isReverse ? this.hex.toUpperCase() : this.otherString;
  }

  /** the page's top band (page-band.scss) starts from the colour (owner 2026-10-12, as on the color tool) */
  @HostBinding( 'style.--band' ) get band(): string {
    return `color-mix(in oklab, ${ this.hex || 'var(--theme-color)' } 55%, var(--background-color))`;
  }

  private sub?: Subscription;

  private paramSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private colorService: ColorService,
    private seoService: SeoService,
    private clipboard: Clipboard,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.sub = this.route.data.subscribe( data => {
      const slug = ( data[ 'converter' ] as string ) || 'hex-to-rgb';
      this.def = CONVERTERS[ slug ] || CONVERTERS[ 'hex-to-rgb' ];
      this.isReverse = this.def.slug.endsWith( '-to-hex' );
      this.faq = converterFaq( this.def.fromLabel, this.def.toLabel );
      this.hex = this.def.sample.toLowerCase();
      this.otherConverters = CONVERTER_LIST.filter( c => c.slug !== this.def.slug );
      this.pageSections = [
        { key: 'formats', label: `${ this.def.toLabel } Formats` },
        { key: 'howitworks', label: `How ${ this.def.fromLabel } to ${ this.def.toLabel } Works` },
        { key: 'more', label: 'More Color Tools' },
        { key: 'themes', label: 'Related Themes' },
        { key: 'blog', label: 'From The Blog' },
        { key: 'about', label: 'Questions' },
        { key: 'reviews', label: 'Reviews' },
      ];
      this.refresh();
      this.dataReady = true;
    } );
    // The optional :value segment carries a specific conversion (549eff on
    // hex-to-rgb, 234-234-101 on rgb-to-hex) so every conversion is a
    // shareable URL. An invalid value 404s like the color tool.
    this.paramSub = this.route.paramMap.subscribe( params => {
      const value = params.get( 'value' );
      if ( !value ) {
        // A blank visit renders a random named color IN PLACE: the router
        // state moves to the valued route but the address bar stays on the
        // bare landing URL, so crawlers never see a JS redirect off it.
        const named = this.colorService.getHexCode( this.colorService.getRandomNamedColor() )
          || this.def.sample.replace( '#', '' );
        this.router.navigate( [ this.valueUrl( '#' + named ) ], { skipLocationChange: true } );
        return;
      }
      const hex = this.parseValue( value );
      if ( !hex ) {
        this.router.navigate( [ URLS.notFound ] );
        return;
      }
      this.hex = hex;
      this.refresh();
      this.refreshSeo();
    } );
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) {
      return;
    }
    // Scroll-spy: highlight the sidebar entry for whichever section is centered.
    this.sectionObserver = new IntersectionObserver( entries => {
      let changed = false;
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) {
            this.zone.run( () => ( this.activeSection = sec ) );
            changed = true;
          }
        }
      }
      if ( changed ) {
        this.scrollActivePillToStart();
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    // Sections render after dataReady flips; observe on the next tick. The hero
    // ([data-section="top"]) is observed too so the colour pill is the selected
    // entry while the page is at the top.
    setTimeout( () => {
      document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) );
    } );
  }

  ngOnDestroy(): void {
    this.sectionObserver?.disconnect();
    this.sub?.unsubscribe();
    this.paramSub?.unsubscribe();
  }

  onWidgetColor( hex: string ): void {
    this.hex = hex;
    this.refresh();
    // Keep the URL shareable without a route re-fire (the user is already at
    // the hero, so no scroll-to-top either), like the color tool's edits. The
    // route interceptor doesn't re-run on location.go, so the head is
    // refreshed by hand to keep the title in step with the URL.
    this.location.go( this.valueUrl( hex ) );
    this.refreshSeo();
  }

  useExample( hex: string ): void {
    // A real navigation, so the global scroll-to-top brings the hero (and the
    // converted result) into view.
    this.router.navigate( [ this.valueUrl( hex ) ] );
  }

  // /tools/hex-to-rgb/549eff, /tools/rgb-to-hex/84-158-255,
  // /tools/hex-to-hsl/549eff or /tools/hsl-to-hex/214-100-66 for a color.
  private valueUrl( hex: string ): string {
    const clean = hex.replace( '#', '' ).toLowerCase();
    const family = this.def.family;
    const base = this.isReverse
      ? ( family === 'hsl' ? URLS.tools.hslToHex : family === 'hsv' ? URLS.tools.hsvToHex : family === 'hsb' ? URLS.tools.hsbToHex : family === 'hwb' ? URLS.tools.hwbToHex : family === 'cmyk' ? URLS.tools.cmykToHex : family === 'oklch' ? URLS.tools.oklchToHex : family === 'oklab' ? URLS.tools.oklabToHex : family === 'lab' ? URLS.tools.cielabToHex : family === 'lch' ? URLS.tools.cielchToHex : family === 'luv' ? URLS.tools.cieluvToHex : family === 'p3' ? URLS.tools.displayP3ToHex : family === 'xyz' ? URLS.tools.xyzToHex : family === 'yxy' ? URLS.tools.yxyToHex : family === 'hunter' ? URLS.tools.hunterLabToHex : URLS.tools.rgbToHex )
      : ( family === 'hsl' ? URLS.tools.hexToHsl : family === 'hsv' ? URLS.tools.hexToHsv : family === 'hsb' ? URLS.tools.hexToHsb : family === 'hwb' ? URLS.tools.hexToHwb : family === 'cmyk' ? URLS.tools.hexToCmyk : family === 'oklch' ? URLS.tools.hexToOklch : family === 'oklab' ? URLS.tools.hexToOklab : family === 'lab' ? URLS.tools.hexToCielab : family === 'lch' ? URLS.tools.hexToCielch : family === 'luv' ? URLS.tools.hexToCieluv : family === 'p3' ? URLS.tools.hexToDisplayP3 : family === 'xyz' ? URLS.tools.hexToXyz : family === 'yxy' ? URLS.tools.hexToYxy : family === 'hunter' ? URLS.tools.hexToHunterLab : URLS.tools.hexToRgb );
    if ( !this.isReverse ) {
      return base + '/' + clean;
    }
    if ( family === 'hsl' ) {
      const hsl = this.colorService.hexToHslInts( clean );
      return base + '/' + [ hsl.h, hsl.s, hsl.l ].join( '-' );
    }
    if ( family === 'hsv' || family === 'hsb' ) {
      const hsv = this.colorService.hexToHsv( clean );
      return base + '/' + [ hsv[ 0 ], hsv[ 1 ], hsv[ 2 ] ].join( '-' );
    }
    if ( family === 'hwb' ) {
      const hwb = this.colorService.hexToHwb( clean );
      return base + '/' + [ hwb[ 0 ], hwb[ 1 ], hwb[ 2 ] ].join( '-' );
    }
    if ( family === 'cmyk' ) {
      const cmyk = this.colorService.hexToCmyk( clean ).items;
      return base + '/' + cmyk.map( ( i: { value: number } ) => i.value ).join( '-' );
    }
    if ( family === 'oklch' ) {
      // L as an integer percent, chroma scaled x1000, hue rounded to a degree,
      // so /tools/oklch-to-hex/63-258-29 stays an integer-dash slug like the rest.
      const o = this.colorService.hexToOklch( clean );
      return base + '/' + [ Math.round( o[ 0 ] * 100 ), Math.round( o[ 1 ] * 1000 ), Math.round( o[ 2 ] ) ].join( '-' );
    }
    if ( family === 'oklab' ) {
      // a and b are signed, so they are x1000 then offset by +500 (500 = zero) to
      // stay unsigned integers in the dash slug: /tools/oklab-to-hex/63-725-626.
      const o = this.colorService.hexToOklab( clean );
      return base + '/' + [ Math.round( o[ 0 ] * 100 ), Math.round( o[ 1 ] * 1000 ) + 500, Math.round( o[ 2 ] * 1000 ) + 500 ].join( '-' );
    }
    if ( family === 'lab' ) {
      // CIELAB a/b are signed and roughly +/-128; rounded to a degree and offset
      // by +200 (200 = zero) so the slug stays unsigned: /tools/cielab-to-hex/53-280-267.
      const o = this.colorService.hexToLab( clean );
      return base + '/' + [ Math.round( o[ 0 ] ), Math.round( o[ 1 ] ) + 200, Math.round( o[ 2 ] ) + 200 ].join( '-' );
    }
    if ( family === 'lch' ) {
      // CIELCH: L, chroma (0..~150) and hue in degrees, all non-negative integers.
      const o = this.colorService.hexToLch( clean );
      return base + '/' + [ Math.round( o[ 0 ] ), Math.round( o[ 1 ] ), Math.round( o[ 2 ] ) ].join( '-' );
    }
    if ( family === 'luv' ) {
      // CIELUV u/v are signed (u about -84..175, v about -134..107 inside sRGB);
      // rounded and offset by +200 (200 = zero) like CIELAB: /tools/cieluv-to-hex/53-375-238.
      const o = this.colorService.hexToLuv( clean );
      return base + '/' + [ Math.round( o[ 0 ] ), Math.round( o[ 1 ] ) + 200, Math.round( o[ 2 ] ) + 200 ].join( '-' );
    }
    if ( family === 'p3' ) {
      // Display P3: the three 0..1 channels as ten-thousandths, so the slug
      // round-trips the 4-decimal display exactly: /tools/display-p3-to-hex/9175-2003-1386.
      const o = this.colorService.hexToDisplayP3( clean );
      return base + '/' + o.map( c => Math.round( c * 10000 ) ).join( '-' );
    }
    if ( family === 'xyz' ) {
      // CIE XYZ on the 0..100 scale as hundredths: /tools/xyz-to-hex/4125-2127-193.
      const o = this.colorService.hexToXyz( clean );
      return base + '/' + o.map( ( c: number ) => Math.round( c * 10000 ) ).join( '-' );
    }
    if ( family === 'yxy' ) {
      // Yxy: Y (0..100) as hundredths, x and y as ten-thousandths.
      const o = this.colorService.hexToYxy( clean );
      return base + '/' + [ Math.round( o[ 0 ] * 100 ), Math.round( o[ 1 ] * 10000 ), Math.round( o[ 2 ] * 10000 ) ].join( '-' );
    }
    if ( family === 'hunter' ) {
      // Hunter Lab: L, then a and b offset by 200 so they stay unsigned integers (like CIELAB).
      const o = this.colorService.hexToHunterLab( clean );
      return base + '/' + [ Math.round( o[ 0 ] ), Math.round( o[ 1 ] ) + 200, Math.round( o[ 2 ] ) + 200 ].join( '-' );
    }
    const num = parseInt( clean, 16 );
    return base + '/' + [ ( num >> 16 ) & 255, ( num >> 8 ) & 255, num & 255 ].join( '-' );
  }

  // Per-conversion head, mirroring the server-injected meta in
  // seo-route-meta.js (converterSeo) so the client-rendered title/description
  // match the crawled one exactly, like the color tool's refreshSeo.
  private refreshSeo(): void {
    // Blank landings render at the bare converter URL (skipLocationChange);
    // its static landing meta must survive, so only real param URLs get the
    // per-conversion head.
    const family = this.def.family;
    const base = this.isReverse
      ? ( family === 'hsl' ? URLS.tools.hslToHex : family === 'hsv' ? URLS.tools.hsvToHex : family === 'hsb' ? URLS.tools.hsbToHex : family === 'hwb' ? URLS.tools.hwbToHex : family === 'cmyk' ? URLS.tools.cmykToHex : family === 'oklch' ? URLS.tools.oklchToHex : family === 'oklab' ? URLS.tools.oklabToHex : family === 'lab' ? URLS.tools.cielabToHex : family === 'lch' ? URLS.tools.cielchToHex : family === 'luv' ? URLS.tools.cieluvToHex : family === 'p3' ? URLS.tools.displayP3ToHex : family === 'xyz' ? URLS.tools.xyzToHex : family === 'yxy' ? URLS.tools.yxyToHex : family === 'hunter' ? URLS.tools.hunterLabToHex : URLS.tools.rgbToHex )
      : ( family === 'hsl' ? URLS.tools.hexToHsl : family === 'hsv' ? URLS.tools.hexToHsv : family === 'hsb' ? URLS.tools.hexToHsb : family === 'hwb' ? URLS.tools.hexToHwb : family === 'cmyk' ? URLS.tools.hexToCmyk : family === 'oklch' ? URLS.tools.hexToOklch : family === 'oklab' ? URLS.tools.hexToOklab : family === 'lab' ? URLS.tools.hexToCielab : family === 'lch' ? URLS.tools.hexToCielch : family === 'luv' ? URLS.tools.hexToCieluv : family === 'p3' ? URLS.tools.hexToDisplayP3 : family === 'xyz' ? URLS.tools.hexToXyz : family === 'yxy' ? URLS.tools.hexToYxy : family === 'hunter' ? URLS.tools.hexToHunterLab : URLS.tools.hexToRgb );
    if ( !this.location.path().split( '?' )[ 0 ].startsWith( base + '/' ) ) {
      return;
    }
    const hex = ( this.hex || '' ).replace( '#', '' ).toUpperCase();
    if ( !/^[0-9A-F]{6}$/.test( hex ) ) {
      return;
    }
    const num = parseInt( hex, 16 );
    const r = ( num >> 16 ) & 255;
    const g = ( num >> 8 ) & 255;
    const b = num & 255;
    const hsl = this.colorService.hexToHslInts( hex );
    const hsv = this.colorService.hexToHsv( hex );
    const hwb = this.colorService.hexToHwb( hex );
    const cmyk = this.colorService.hexToCmyk( hex ).items.map( ( i: { value: number } ) => i.value );
    const oklch = this.colorService.hexToOklch( hex );
    const oklab = this.colorService.hexToOklab( hex );
    const lab = this.colorService.hexToLab( hex );
    const lch = this.colorService.hexToLch( hex );
    const luv = this.colorService.hexToLuv( hex );
    const p3 = this.colorService.hexToDisplayP3( hex );
    const xyz = this.colorService.hexToXyz( hex ).map( ( n: number ) => Math.round( n * 10000 ) / 100 );
    const yxy = this.colorService.hexToYxy( hex );
    const hunter = this.colorService.hexToHunterLab( hex );
    const otherStr = family === 'hsl'
      ? `hsl(${ hsl.h }, ${ hsl.s }%, ${ hsl.l }%)`
      : family === 'hsv' || family === 'hsb'
        ? `${ family }(${ hsv[ 0 ] }, ${ hsv[ 1 ] }%, ${ hsv[ 2 ] }%)`
        : family === 'hwb'
          ? `hwb(${ hwb[ 0 ] } ${ hwb[ 1 ] }% ${ hwb[ 2 ] }%)`
          : family === 'cmyk'
            ? `cmyk(${ cmyk[ 0 ] }%, ${ cmyk[ 1 ] }%, ${ cmyk[ 2 ] }%, ${ cmyk[ 3 ] }%)`
            : family === 'oklch'
              ? `oklch(${ Math.round( oklch[ 0 ] * 100 ) }% ${ Math.round( oklch[ 1 ] * 1000 ) / 1000 } ${ Math.round( oklch[ 2 ] ) })`
              : family === 'oklab'
                ? `oklab(${ Math.round( oklab[ 0 ] * 100 ) }% ${ Math.round( oklab[ 1 ] * 1000 ) / 1000 } ${ Math.round( oklab[ 2 ] * 1000 ) / 1000 })`
                : family === 'lab'
                  ? `lab(${ Math.round( lab[ 0 ] ) }% ${ Math.round( lab[ 1 ] * 100 ) / 100 } ${ Math.round( lab[ 2 ] * 100 ) / 100 })`
                  : family === 'lch'
                    ? `lch(${ Math.round( lch[ 0 ] ) }% ${ Math.round( lch[ 1 ] * 100 ) / 100 } ${ Math.round( lch[ 2 ] ) })`
                    : family === 'luv'
                    ? `luv(${ luv[ 0 ] }, ${ luv[ 1 ] }, ${ luv[ 2 ] })`
                    : family === 'p3'
                      ? `color(display-p3 ${ p3[ 0 ] } ${ p3[ 1 ] } ${ p3[ 2 ] })`
                      : family === 'xyz'
                        ? `xyz(${ xyz[ 0 ] }, ${ xyz[ 1 ] }, ${ xyz[ 2 ] })`
                        : family === 'yxy'
                          ? `yxy(${ yxy[ 0 ] }, ${ yxy[ 1 ] }, ${ yxy[ 2 ] })`
                          : family === 'hunter'
                            ? `hunter-lab(${ hunter[ 0 ] }, ${ hunter[ 1 ] }, ${ hunter[ 2 ] })`
                            : `rgb(${ r }, ${ g }, ${ b })`;
    const from = this.isReverse ? otherStr : '#' + hex;
    const to = this.isReverse ? '#' + hex : otherStr;
    const toLabel = this.def.toLabel;
    this.seoService.setMetaData( {
      title: `${ from } to ${ toLabel } - ${ to } | Codigrate`,
      description: `${ from } converted to ${ toLabel } is ${ to }. See the live preview, every common ${ toLabel } notation and the worked channel-by-channel conversion on Codigrate's free converter.`,
      keywords: [
        `${ from.toLowerCase() } to ${ toLabel.toLowerCase() }`,
        `${ hex.toLowerCase() } ${ this.isReverse ? 'hex' : this.def.family }`,
        to.toLowerCase(),
        this.def.slug.replace( /-/g, ' ' ),
        'color converter', 'hex color code', this.def.family === 'rgb' ? 'rgb color' : this.def.family + ' color', 'vibe coding', 'codigrate',
      ],
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
  }

  // The route :value back to a hex, or null when malformed (each direction only
  // accepts its own format; CMYK is the four segment one).
  private parseValue( value: string ): string | null {
    if ( this.isReverse ) {
      if ( this.def.family === 'cmyk' ) {
        const m4 = value.match( /^(\d{1,3})-(\d{1,3})-(\d{1,3})-(\d{1,3})$/ );
        if ( !m4 ) {
          return null;
        }
        const inks = [ Number( m4[ 1 ] ), Number( m4[ 2 ] ), Number( m4[ 3 ] ), Number( m4[ 4 ] ) ];
        if ( inks.some( c => c > 100 ) ) {
          return null;
        }
        return '#' + this.colorService.cmykToHex( inks[ 0 ], inks[ 1 ], inks[ 2 ], inks[ 3 ] ).toLowerCase();
      }
      if ( this.def.family === 'oklch' ) {
        // L is an integer percent, chroma is x1000, hue is degrees (matches valueUrl).
        const mo = value.match( /^(\d{1,3})-(\d{1,3})-(\d{1,3})$/ );
        if ( !mo ) {
          return null;
        }
        const L = Number( mo[ 1 ] ), Cx = Number( mo[ 2 ] ), H = Number( mo[ 3 ] );
        if ( L > 100 || Cx > 500 || H > 360 ) {
          return null;
        }
        return '#' + this.colorService.oklchToHex( L / 100, Cx / 1000, H ).toLowerCase();
      }
      if ( this.def.family === 'oklab' ) {
        // L integer percent; a and b are x1000 with a +500 zero offset (matches valueUrl).
        const mo = value.match( /^(\d{1,3})-(\d{1,4})-(\d{1,4})$/ );
        if ( !mo ) {
          return null;
        }
        const L = Number( mo[ 1 ] ), aEnc = Number( mo[ 2 ] ), bEnc = Number( mo[ 3 ] );
        if ( L > 100 || aEnc > 1000 || bEnc > 1000 ) {
          return null;
        }
        return '#' + this.colorService.oklabToHex( L / 100, ( aEnc - 500 ) / 1000, ( bEnc - 500 ) / 1000 ).toLowerCase();
      }
      if ( this.def.family === 'lab' ) {
        // CIELAB: L integer percent; a and b are integers with a +200 zero offset.
        const mo = value.match( /^(\d{1,3})-(\d{1,3})-(\d{1,3})$/ );
        if ( !mo ) {
          return null;
        }
        const L = Number( mo[ 1 ] ), aEnc = Number( mo[ 2 ] ), bEnc = Number( mo[ 3 ] );
        if ( L > 100 || aEnc > 400 || bEnc > 400 ) {
          return null;
        }
        return '#' + this.colorService.labToHex( L, aEnc - 200, bEnc - 200 ).toLowerCase();
      }
      if ( this.def.family === 'lch' ) {
        // CIELCH: L, chroma, hue as non-negative integers (matches valueUrl).
        const mo = value.match( /^(\d{1,3})-(\d{1,3})-(\d{1,3})$/ );
        if ( !mo ) {
          return null;
        }
        const L = Number( mo[ 1 ] ), C = Number( mo[ 2 ] ), H = Number( mo[ 3 ] );
        if ( L > 100 || C > 200 || H > 360 ) {
          return null;
        }
        return '#' + this.colorService.lchToHex( L, C, H ).toLowerCase();
      }
      if ( this.def.family === 'luv' ) {
        // CIELUV: L, u + 200, v + 200 (matches valueUrl).
        const mo = value.match( /^(\d{1,3})-(\d{1,3})-(\d{1,3})$/ );
        if ( !mo ) {
          return null;
        }
        const L = Number( mo[ 1 ] ), uEnc = Number( mo[ 2 ] ), vEnc = Number( mo[ 3 ] );
        if ( L > 100 || uEnc > 400 || vEnc > 400 ) {
          return null;
        }
        return '#' + this.colorService.luvToHex( L, uEnc - 200, vEnc - 200 ).toLowerCase();
      }
      if ( this.def.family === 'p3' ) {
        // Display P3 channels as ten-thousandths of 0..1 (matches valueUrl).
        const mo = value.match( /^(\d{1,5})-(\d{1,5})-(\d{1,5})$/ );
        if ( !mo ) {
          return null;
        }
        const r = Number( mo[ 1 ] ), g = Number( mo[ 2 ] ), b = Number( mo[ 3 ] );
        if ( r > 10000 || g > 10000 || b > 10000 ) {
          return null;
        }
        return '#' + this.colorService.displayP3ToHex( r / 10000, g / 10000, b / 10000 ).toLowerCase();
      }
      if ( this.def.family === 'xyz' ) {
        // CIE XYZ (0..100 scale) as hundredths; Z reaches ~109 for white.
        const mo = value.match( /^(\d{1,5})-(\d{1,5})-(\d{1,5})$/ );
        if ( !mo ) {
          return null;
        }
        const X = Number( mo[ 1 ] ), Y = Number( mo[ 2 ] ), Z = Number( mo[ 3 ] );
        if ( X > 11000 || Y > 10000 || Z > 11000 ) {
          return null;
        }
        return '#' + this.colorService.xyzToHex( X / 10000, Y / 10000, Z / 10000 ).toLowerCase();
      }
      if ( this.def.family === 'yxy' ) {
        // Yxy: Y as hundredths of 0..100, chromaticity x and y as ten-thousandths of 0..1.
        const mo = value.match( /^(\d{1,5})-(\d{1,5})-(\d{1,5})$/ );
        if ( !mo ) {
          return null;
        }
        const Y = Number( mo[ 1 ] ), x = Number( mo[ 2 ] ), y = Number( mo[ 3 ] );
        if ( Y > 10000 || x > 10000 || y > 10000 ) {
          return null;
        }
        return '#' + this.colorService.yxyToHex( Y / 100, x / 10000, y / 10000 ).toLowerCase();
      }
      if ( this.def.family === 'hunter' ) {
        // Hunter Lab: L, a + 200, b + 200 (matches valueUrl).
        const mo = value.match( /^(\d{1,3})-(\d{1,3})-(\d{1,3})$/ );
        if ( !mo ) {
          return null;
        }
        const L = Number( mo[ 1 ] ), aEnc = Number( mo[ 2 ] ), bEnc = Number( mo[ 3 ] );
        if ( L > 100 || aEnc > 400 || bEnc > 400 ) {
          return null;
        }
        return '#' + this.colorService.hunterLabToHex( L, aEnc - 200, bEnc - 200 ).toLowerCase();
      }
      const m = value.match( /^(\d{1,3})-(\d{1,3})-(\d{1,3})$/ );
      if ( !m ) {
        return null;
      }
      const channels = [ Number( m[ 1 ] ), Number( m[ 2 ] ), Number( m[ 3 ] ) ];
      if ( this.def.family !== 'rgb' ) {
        if ( channels[ 0 ] > 360 || channels[ 1 ] > 100 || channels[ 2 ] > 100 ) {
          return null;
        }
        const converted = this.def.family === 'hsl'
          ? this.colorService.hslToHex( channels[ 0 ], channels[ 1 ], channels[ 2 ] )
          : this.def.family === 'hwb'
            ? this.colorService.hwbToHex( channels[ 0 ], channels[ 1 ], channels[ 2 ] )
            : this.colorService.hsvToHex( channels[ 0 ], channels[ 1 ], channels[ 2 ] );
        return '#' + converted.toLowerCase();
      }
      if ( channels.some( c => c > 255 ) ) {
        return null;
      }
      return '#' + channels.map( c => c.toString( 16 ).padStart( 2, '0' ) ).join( '' );
    }
    return /^[0-9a-fA-F]{6}$/.test( value ) ? '#' + value.toLowerCase() : null;
  }

  // Worked HSL derivation with the current color's real numbers. Forward
  // (hex-to-hsl) walks rgb -> H/S/L; reverse (hsl-to-hex) walks H/S/L -> rgb,
  // whose channels the base-16 table below then turns into hex pairs.
  private buildHslSteps( r: number, g: number, b: number, hsl: { h: number; s: number; l: number } ): { label: string; expr: string }[] {
    const f3 = ( n: number ): string => ( Math.round( n * 1000 ) / 1000 ).toString();
    if ( !this.isReverse ) {
      const rn = r / 255, gn = g / 255, bn = b / 255;
      const max = Math.max( rn, gn, bn );
      const min = Math.min( rn, gn, bn );
      const delta = max - min;
      const lRaw = ( max + min ) / 2;
      const dominant = delta === 0 ? '' : ( max === rn ? 'Red' : max === gn ? 'Green' : 'Blue' );
      let hueExpr: string;
      if ( delta === 0 ) {
        hueExpr = 'all channels are equal, so the color is gray and the hue defaults to 0°';
      } else if ( dominant === 'Red' ) {
        hueExpr = `red is largest → H = 60 × ((g − b) / Δ mod 6) = 60 × ((${ f3( gn ) } − ${ f3( bn ) }) / ${ f3( delta ) }) = ${ hsl.h }°`;
      } else if ( dominant === 'Green' ) {
        hueExpr = `green is largest → H = 60 × ((b − r) / Δ + 2) = 60 × ((${ f3( bn ) } − ${ f3( rn ) }) / ${ f3( delta ) } + 2) = ${ hsl.h }°`;
      } else {
        hueExpr = `blue is largest → H = 60 × ((r − g) / Δ + 4) = 60 × ((${ f3( rn ) } − ${ f3( gn ) }) / ${ f3( delta ) } + 4) = ${ hsl.h }°`;
      }
      return [
        { label: 'Normalize', expr: `divide each channel by 255 → r = ${ f3( rn ) }, g = ${ f3( gn ) }, b = ${ f3( bn ) }` },
        { label: 'Max / Min', expr: `max = ${ f3( max ) }, min = ${ f3( min ) }, Δ = max − min = ${ f3( delta ) }` },
        { label: 'Lightness', expr: `L = (max + min) / 2 = (${ f3( max ) } + ${ f3( min ) }) / 2 = ${ f3( lRaw ) } → ${ hsl.l }%` },
        { label: 'Saturation', expr: delta === 0
          ? 'Δ = 0, so saturation is 0% (a pure gray)'
          : `S = Δ / (1 − |2L − 1|) = ${ f3( delta ) } / ${ f3( 1 - Math.abs( 2 * lRaw - 1 ) ) } = ${ hsl.s }%` },
        { label: 'Hue', expr: hueExpr },
      ];
    }
    const sN = hsl.s / 100, lN = hsl.l / 100;
    const c = ( 1 - Math.abs( 2 * lN - 1 ) ) * sN;
    const x = c * ( 1 - Math.abs( ( hsl.h / 60 ) % 2 - 1 ) );
    const m = lN - c / 2;
    const sector = Math.floor( ( ( hsl.h % 360 ) + 360 ) % 360 / 60 );
    const sectors = [ '(C, X, 0)', '(X, C, 0)', '(0, C, X)', '(0, X, C)', '(X, 0, C)', '(C, 0, X)' ];
    const sectorRange = `${ sector * 60 }° ≤ H < ${ sector * 60 + 60 }°`;
    return [
      { label: 'Chroma', expr: `C = (1 − |2L − 1|) × S = ${ f3( 1 - Math.abs( 2 * lN - 1 ) ) } × ${ f3( sN ) } = ${ f3( c ) }` },
      { label: 'X', expr: `X = C × (1 − |(H / 60) mod 2 − 1|) = ${ f3( x ) }` },
      { label: 'Match', expr: `m = L − C / 2 = ${ f3( lN ) } − ${ f3( c / 2 ) } = ${ f3( m ) }` },
      { label: 'Sector', expr: `${ sectorRange }, so (r′, g′, b′) = ${ sectors[ sector ] }` },
      { label: 'Channels', expr: `add m and scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  // Worked HSV derivation with the current color's real numbers, the value
  // sibling of buildHslSteps.
  private buildHsvSteps( r: number, g: number, b: number, hsv: { h: number; s: number; v: number } ): { label: string; expr: string }[] {
    const f3 = ( n: number ): string => ( Math.round( n * 1000 ) / 1000 ).toString();
    // HSB is HSV with value renamed to brightness; the worked math follows suit.
    const vName = this.def.family === 'hsb' ? 'Brightness' : 'Value';
    const vSym = this.def.family === 'hsb' ? 'B' : 'V';
    if ( !this.isReverse ) {
      const rn = r / 255, gn = g / 255, bn = b / 255;
      const max = Math.max( rn, gn, bn );
      const min = Math.min( rn, gn, bn );
      const delta = max - min;
      const dominant = delta === 0 ? '' : ( max === rn ? 'Red' : max === gn ? 'Green' : 'Blue' );
      let hueExpr: string;
      if ( delta === 0 ) {
        hueExpr = 'all channels are equal, so the color is gray and the hue defaults to 0°';
      } else if ( dominant === 'Red' ) {
        hueExpr = `red is largest → H = 60 × ((g − b) / Δ mod 6) = 60 × ((${ f3( gn ) } − ${ f3( bn ) }) / ${ f3( delta ) }) = ${ hsv.h }°`;
      } else if ( dominant === 'Green' ) {
        hueExpr = `green is largest → H = 60 × ((b − r) / Δ + 2) = 60 × ((${ f3( bn ) } − ${ f3( rn ) }) / ${ f3( delta ) } + 2) = ${ hsv.h }°`;
      } else {
        hueExpr = `blue is largest → H = 60 × ((r − g) / Δ + 4) = 60 × ((${ f3( rn ) } − ${ f3( gn ) }) / ${ f3( delta ) } + 4) = ${ hsv.h }°`;
      }
      return [
        { label: 'Normalize', expr: `divide each channel by 255 → r = ${ f3( rn ) }, g = ${ f3( gn ) }, b = ${ f3( bn ) }` },
        { label: 'Max / Min', expr: `max = ${ f3( max ) }, min = ${ f3( min ) }, Δ = max − min = ${ f3( delta ) }` },
        { label: vName, expr: `${ vSym } is simply the largest channel → ${ vSym } = max = ${ f3( max ) } → ${ hsv.v }%` },
        { label: 'Saturation', expr: max === 0
          ? 'the color is black, so saturation is 0%'
          : `S = Δ / max = ${ f3( delta ) } / ${ f3( max ) } = ${ hsv.s }%` },
        { label: 'Hue', expr: hueExpr },
      ];
    }
    const sN = hsv.s / 100, vN = hsv.v / 100;
    const c = vN * sN;
    const x = c * ( 1 - Math.abs( ( hsv.h / 60 ) % 2 - 1 ) );
    const m = vN - c;
    const sector = Math.floor( ( ( hsv.h % 360 ) + 360 ) % 360 / 60 );
    const sectors = [ '(C, X, 0)', '(X, C, 0)', '(0, C, X)', '(0, X, C)', '(X, 0, C)', '(C, 0, X)' ];
    return [
      { label: 'Chroma', expr: `C = ${ vSym } × S = ${ f3( vN ) } × ${ f3( sN ) } = ${ f3( c ) }` },
      { label: 'X', expr: `X = C × (1 − |(H / 60) mod 2 − 1|) = ${ f3( x ) }` },
      { label: 'Match', expr: `m = ${ vSym } − C = ${ f3( vN ) } − ${ f3( c ) } = ${ f3( m ) }` },
      { label: 'Sector', expr: `${ sector * 60 }° ≤ H < ${ sector * 60 + 60 }°, so (r′, g′, b′) = ${ sectors[ sector ] }` },
      { label: 'Channels', expr: `add m and scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  // Worked HWB derivation with the current color's real numbers. Forward is
  // the simplest of the family (whiteness = min channel, blackness = 1 - max);
  // reverse mixes the pure hue toward white and black.
  private buildHwbSteps( r: number, g: number, b: number, hwb: { h: number; w: number; b: number } ): { label: string; expr: string }[] {
    const f3 = ( n: number ): string => ( Math.round( n * 1000 ) / 1000 ).toString();
    if ( !this.isReverse ) {
      const rn = r / 255, gn = g / 255, bn = b / 255;
      const max = Math.max( rn, gn, bn );
      const min = Math.min( rn, gn, bn );
      const delta = max - min;
      const dominant = delta === 0 ? '' : ( max === rn ? 'Red' : max === gn ? 'Green' : 'Blue' );
      let hueExpr: string;
      if ( delta === 0 ) {
        hueExpr = 'all channels are equal, so the color is gray and the hue defaults to 0°';
      } else if ( dominant === 'Red' ) {
        hueExpr = `red is largest → H = 60 × ((g − b) / Δ mod 6) = 60 × ((${ f3( gn ) } − ${ f3( bn ) }) / ${ f3( delta ) }) = ${ hwb.h }°`;
      } else if ( dominant === 'Green' ) {
        hueExpr = `green is largest → H = 60 × ((b − r) / Δ + 2) = 60 × ((${ f3( bn ) } − ${ f3( rn ) }) / ${ f3( delta ) } + 2) = ${ hwb.h }°`;
      } else {
        hueExpr = `blue is largest → H = 60 × ((r − g) / Δ + 4) = 60 × ((${ f3( rn ) } − ${ f3( gn ) }) / ${ f3( delta ) } + 4) = ${ hwb.h }°`;
      }
      return [
        { label: 'Normalize', expr: `divide each channel by 255 → r = ${ f3( rn ) }, g = ${ f3( gn ) }, b = ${ f3( bn ) }` },
        { label: 'Max / Min', expr: `max = ${ f3( max ) }, min = ${ f3( min ) }, Δ = max − min = ${ f3( delta ) }` },
        { label: 'Whiteness', expr: `W is simply the smallest channel → W = min = ${ f3( min ) } → ${ hwb.w }%` },
        { label: 'Blackness', expr: `B is how far the largest channel falls short of full → B = 1 − max = ${ f3( 1 - max ) } → ${ hwb.b }%` },
        { label: 'Hue', expr: hueExpr },
      ];
    }
    const wN = hwb.w / 100, bN = hwb.b / 100;
    if ( wN + bN >= 1 ) {
      return [
        { label: 'Overflow', expr: `W + B = ${ f3( wN ) } + ${ f3( bN ) } ≥ 1, so no room is left for the hue` },
        { label: 'Gray', expr: `the color is the gray W / (W + B) = ${ f3( wN / ( wN + bN ) ) } on every channel` },
        { label: 'Channels', expr: `scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
      ];
    }
    const x = 1 - Math.abs( ( hwb.h / 60 ) % 2 - 1 );
    const scale = 1 - wN - bN;
    const sector = Math.floor( ( ( hwb.h % 360 ) + 360 ) % 360 / 60 );
    const sectors = [ '(1, X, 0)', '(X, 1, 0)', '(0, 1, X)', '(0, X, 1)', '(X, 0, 1)', '(1, 0, X)' ];
    return [
      { label: 'Pure Hue', expr: `at full strength C = 1 and X = 1 − |(H / 60) mod 2 − 1| = ${ f3( x ) }` },
      { label: 'Sector', expr: `${ sector * 60 }° ≤ H < ${ sector * 60 + 60 }°, so (r′, g′, b′) = ${ sectors[ sector ] }` },
      { label: 'Scale', expr: `the room left for the hue is 1 − W − B = 1 − ${ f3( wN ) } − ${ f3( bN ) } = ${ f3( scale ) }` },
      { label: 'Mix', expr: `each channel = pure × ${ f3( scale ) } + W (whiteness lifts the floor, blackness lowers the ceiling)` },
      { label: 'Channels', expr: `scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  // Worked CMYK derivation with the current color's real numbers. Forward walks
  // rgb -> key -> ink coverages; reverse removes each ink and the shared key
  // from the paper's full brightness.
  private buildCmykSteps( r: number, g: number, b: number, cmyk: number[] ): { label: string; expr: string }[] {
    const f3 = ( n: number ): string => ( Math.round( n * 1000 ) / 1000 ).toString();
    const rn = r / 255, gn = g / 255, bn = b / 255;
    if ( !this.isReverse ) {
      const kRaw = 1 - Math.max( rn, gn, bn );
      if ( kRaw === 1 ) {
        return [
          { label: 'Normalize', expr: 'every channel is 0, the color is pure black' },
          { label: 'Key', expr: 'K = 1 − max = 1 → 100%, and the three inks stay at 0% (the key plate does all the work)' },
        ];
      }
      return [
        { label: 'Normalize', expr: `divide each channel by 255 → r = ${ f3( rn ) }, g = ${ f3( gn ) }, b = ${ f3( bn ) }` },
        { label: 'Key', expr: `the shared black plate covers what every ink would repeat → K = 1 − max = 1 − ${ f3( Math.max( rn, gn, bn ) ) } = ${ f3( kRaw ) } → ${ cmyk[ 3 ] }%` },
        { label: 'Cyan', expr: `C = (1 − r − K) / (1 − K) = (1 − ${ f3( rn ) } − ${ f3( kRaw ) }) / ${ f3( 1 - kRaw ) } = ${ cmyk[ 0 ] }%` },
        { label: 'Magenta', expr: `M = (1 − g − K) / (1 − K) = (1 − ${ f3( gn ) } − ${ f3( kRaw ) }) / ${ f3( 1 - kRaw ) } = ${ cmyk[ 1 ] }%` },
        { label: 'Yellow', expr: `Y = (1 − b − K) / (1 − K) = (1 − ${ f3( bn ) } − ${ f3( kRaw ) }) / ${ f3( 1 - kRaw ) } = ${ cmyk[ 2 ] }%` },
      ];
    }
    const kN = cmyk[ 3 ] / 100;
    return [
      { label: 'Normalize', expr: `divide each value by 100 → C = ${ f3( cmyk[ 0 ] / 100 ) }, M = ${ f3( cmyk[ 1 ] / 100 ) }, Y = ${ f3( cmyk[ 2 ] / 100 ) }, K = ${ f3( kN ) }` },
      { label: 'Red', expr: `R = 255 × (1 − C) × (1 − K) = 255 × ${ f3( 1 - cmyk[ 0 ] / 100 ) } × ${ f3( 1 - kN ) } = ${ r }` },
      { label: 'Green', expr: `G = 255 × (1 − M) × (1 − K) = 255 × ${ f3( 1 - cmyk[ 1 ] / 100 ) } × ${ f3( 1 - kN ) } = ${ g }` },
      { label: 'Blue', expr: `B = 255 × (1 − Y) × (1 − K) = 255 × ${ f3( 1 - cmyk[ 2 ] / 100 ) } × ${ f3( 1 - kN ) } = ${ b }` },
    ];
  }

  // Worked OKLCH derivation with the current color's real numbers. Forward walks
  // sRGB -> linear -> the L/M/S cone response -> OKLAB -> polar OKLCH; reverse
  // unwinds OKLCH -> OKLAB -> linear sRGB -> gamma-encoded channels. Working
  // through OKLAB is what keeps equal numeric steps looking like equal
  // perceptual steps, which is the whole reason to use OKLCH.
  private buildOklchSteps( r: number, g: number, b: number, oklch: { l: number; c: number; h: number } ): { label: string; expr: string }[] {
    const f3 = ( n: number ): string => ( Math.round( n * 1000 ) / 1000 ).toString();
    const lin = ( n: number ): number => { const c = n / 255; return c <= 0.04045 ? c / 12.92 : Math.pow( ( c + 0.055 ) / 1.055, 2.4 ); };
    const lPct = Math.round( oklch.l * 100 );
    if ( !this.isReverse ) {
      const rl = lin( r ), gl = lin( g ), bl = lin( b );
      const lCone = 0.4122214708 * rl + 0.5363325363 * gl + 0.0514459929 * bl;
      const mCone = 0.2119034982 * rl + 0.6806995451 * gl + 0.1073969566 * bl;
      const sCone = 0.0883024619 * rl + 0.2817188376 * gl + 0.6299787005 * bl;
      const lr = Math.cbrt( lCone ), mr = Math.cbrt( mCone ), sr = Math.cbrt( sCone );
      const aComp = 1.9779984951 * lr - 2.4285922050 * mr + 0.4505937099 * sr;
      const bComp = 0.0259040371 * lr + 0.7827717662 * mr - 0.8086757660 * sr;
      return [
        { label: 'Linearize', expr: `undo the sRGB gamma on each channel → r = ${ f3( rl ) }, g = ${ f3( gl ) }, b = ${ f3( bl ) }` },
        { label: 'Cone response', expr: `mix into the L, M, S cones → l = ${ f3( lCone ) }, m = ${ f3( mCone ) }, s = ${ f3( sCone ) }` },
        { label: 'Cube root', expr: `take the cube root of each cone → l′ = ${ f3( lr ) }, m′ = ${ f3( mr ) }, s′ = ${ f3( sr ) }` },
        { label: 'OKLAB', expr: `L = 0.2105 l′ + 0.7936 m′ − 0.0041 s′ = ${ f3( oklch.l ) } → ${ lPct }%, a = ${ f3( aComp ) }, b = ${ f3( bComp ) }` },
        { label: 'Polar', expr: `C = √(a² + b²) = ${ f3( oklch.c ) } and H = atan2(b, a) = ${ Math.round( oklch.h ) }°` },
      ];
    }
    const hr = oklch.h * Math.PI / 180;
    const a = oklch.c * Math.cos( hr );
    const bComp = oklch.c * Math.sin( hr );
    const l_ = oklch.l + 0.3963377774 * a + 0.2158037573 * bComp;
    const m_ = oklch.l - 0.1055613458 * a - 0.0638541728 * bComp;
    const s_ = oklch.l - 0.0894841775 * a - 1.2914855480 * bComp;
    return [
      { label: 'To OKLAB', expr: `a = C·cos(H) = ${ f3( a ) }, b = C·sin(H) = ${ f3( bComp ) } (L stays ${ f3( oklch.l ) })` },
      { label: 'Inverse mix', expr: `undo the OKLAB matrix → l′ = ${ f3( l_ ) }, m′ = ${ f3( m_ ) }, s′ = ${ f3( s_ ) }` },
      { label: 'Cube', expr: 'cube each cone value and map L, M, S back to linear r, g, b' },
      { label: 'Gamma', expr: 'reapply the sRGB curve and clamp to the [0, 1] gamut' },
      { label: 'Channels', expr: `scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  // Worked OKLAB derivation, the Cartesian sibling of buildOklchSteps. Forward
  // walks sRGB -> linear -> the L/M/S cone response -> OKLAB (L, a, b); reverse
  // unwinds OKLAB -> linear sRGB -> gamma-encoded channels.
  private buildOklabSteps( r: number, g: number, b: number, oklab: { l: number; a: number; b: number } ): { label: string; expr: string }[] {
    const f3 = ( n: number ): string => ( Math.round( n * 1000 ) / 1000 ).toString();
    const lin = ( n: number ): number => { const c = n / 255; return c <= 0.04045 ? c / 12.92 : Math.pow( ( c + 0.055 ) / 1.055, 2.4 ); };
    const lPct = Math.round( oklab.l * 100 );
    if ( !this.isReverse ) {
      const rl = lin( r ), gl = lin( g ), bl = lin( b );
      const lCone = 0.4122214708 * rl + 0.5363325363 * gl + 0.0514459929 * bl;
      const mCone = 0.2119034982 * rl + 0.6806995451 * gl + 0.1073969566 * bl;
      const sCone = 0.0883024619 * rl + 0.2817188376 * gl + 0.6299787005 * bl;
      const lr = Math.cbrt( lCone ), mr = Math.cbrt( mCone ), sr = Math.cbrt( sCone );
      return [
        { label: 'Linearize', expr: `undo the sRGB gamma on each channel → r = ${ f3( rl ) }, g = ${ f3( gl ) }, b = ${ f3( bl ) }` },
        { label: 'Cone response', expr: `mix into the L, M, S cones → l = ${ f3( lCone ) }, m = ${ f3( mCone ) }, s = ${ f3( sCone ) }` },
        { label: 'Cube root', expr: `take the cube root of each cone → l′ = ${ f3( lr ) }, m′ = ${ f3( mr ) }, s′ = ${ f3( sr ) }` },
        { label: 'Lightness', expr: `L = 0.2105 l′ + 0.7936 m′ − 0.0041 s′ = ${ f3( oklab.l ) } → ${ lPct }%` },
        { label: 'a and b', expr: `a = ${ f3( oklab.a ) } (green → red) and b = ${ f3( oklab.b ) } (blue → yellow) from the two opponent mixes` },
      ];
    }
    const l_ = oklab.l + 0.3963377774 * oklab.a + 0.2158037573 * oklab.b;
    const m_ = oklab.l - 0.1055613458 * oklab.a - 0.0638541728 * oklab.b;
    const s_ = oklab.l - 0.0894841775 * oklab.a - 1.2914855480 * oklab.b;
    return [
      { label: 'Inverse mix', expr: `undo the OKLAB matrix → l′ = ${ f3( l_ ) }, m′ = ${ f3( m_ ) }, s′ = ${ f3( s_ ) }` },
      { label: 'Cube', expr: 'cube each cone value and map L, M, S back to linear r, g, b' },
      { label: 'Gamma', expr: 'reapply the sRGB curve and clamp to the [0, 1] gamut' },
      { label: 'Channels', expr: `scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  // Worked CIELAB derivation, the CIE sibling of buildOklabSteps. Forward walks
  // sRGB -> linear -> CIE XYZ (D65) -> the cube-root f() -> L, a, b; reverse
  // unwinds L/a/b -> XYZ -> linear sRGB -> gamma-encoded channels.
  private buildLabSteps( r: number, g: number, b: number, lab: { l: number; a: number; b: number } ): { label: string; expr: string }[] {
    const f3 = ( n: number ): string => ( Math.round( n * 1000 ) / 1000 ).toString();
    const f2 = ( n: number ): string => ( Math.round( n * 100 ) / 100 ).toString();
    const lin = ( n: number ): number => { const c = n / 255; return c <= 0.03928 ? c / 12.92 : Math.pow( ( c + 0.055 ) / 1.055, 2.4 ); };
    if ( !this.isReverse ) {
      const rl = lin( r ), gl = lin( g ), bl = lin( b );
      const X = rl * 0.4124564 + gl * 0.3575761 + bl * 0.1804375;
      const Y = rl * 0.2126729 + gl * 0.7151522 + bl * 0.0721750;
      const Z = rl * 0.0193339 + gl * 0.1191920 + bl * 0.9503041;
      return [
        { label: 'Linearize', expr: `undo the sRGB gamma on each channel → r = ${ f3( rl ) }, g = ${ f3( gl ) }, b = ${ f3( bl ) }` },
        { label: 'To XYZ', expr: `mix into CIE tristimulus values → X = ${ f3( X ) }, Y = ${ f3( Y ) }, Z = ${ f3( Z ) }` },
        { label: 'Normalize', expr: 'divide by the D65 white point (0.95047, 1, 1.08883), then take the cube root f()' },
        { label: 'Lightness', expr: `L = 116·f(Y) − 16 = ${ f2( lab.l ) } → ${ Math.round( lab.l ) }%` },
        { label: 'a and b', expr: `a = 500·(f(X) − f(Y)) = ${ f2( lab.a ) } (green → red), b = 200·(f(Y) − f(Z)) = ${ f2( lab.b ) } (blue → yellow)` },
      ];
    }
    return [
      { label: 'To XYZ', expr: 'invert f(): fy = (L + 16) / 116, fx = a/500 + fy, fz = fy − b/200, then cube and scale by the D65 white point' },
      { label: 'To linear', expr: 'multiply XYZ by the inverse sRGB matrix to get linear r, g, b' },
      { label: 'Gamma', expr: 'reapply the sRGB curve and clamp to the [0, 1] gamut' },
      { label: 'Channels', expr: `scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  // Worked CIELCH derivation, the polar sibling of buildLabSteps. Forward walks
  // sRGB -> CIE XYZ -> CIELAB -> polar CIELCH; reverse unwinds CIELCH -> CIELAB
  // -> XYZ -> linear sRGB.
  private buildLchSteps( r: number, g: number, b: number, lch: { l: number; c: number; h: number } ): { label: string; expr: string }[] {
    const f2 = ( n: number ): string => ( Math.round( n * 100 ) / 100 ).toString();
    if ( !this.isReverse ) {
      const lab = this.colorService.hexToLab( this.hex );
      return [
        { label: 'To XYZ', expr: 'linearize the channels, mix into CIE XYZ, then normalize by the D65 white point' },
        { label: 'To CIELAB', expr: `take the cube root f() and read L = ${ f2( lab[ 0 ] ) }, a = ${ f2( lab[ 1 ] ) }, b = ${ f2( lab[ 2 ] ) }` },
        { label: 'Polar', expr: `C = √(a² + b²) = ${ f2( lch.c ) } and H = atan2(b, a) = ${ Math.round( lch.h ) }° at ${ Math.round( lch.l ) }% lightness` },
      ];
    }
    const hr = lch.h * Math.PI / 180;
    return [
      { label: 'To CIELAB', expr: `a = C·cos(H) = ${ f2( lch.c * Math.cos( hr ) ) }, b = C·sin(H) = ${ f2( lch.c * Math.sin( hr ) ) } (L stays ${ f2( lch.l ) })` },
      { label: 'To XYZ', expr: 'invert the CIELAB f() and scale by the D65 white point' },
      { label: 'To linear', expr: 'multiply XYZ by the inverse sRGB matrix, then reapply the sRGB curve and clamp' },
      { label: 'Channels', expr: `scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  // Worked CIELUV derivation, the u′v′ sibling of buildLabSteps. Forward walks
  // sRGB -> linear -> CIE XYZ (D65) -> L plus the u′v′ chromaticity distance to
  // the white point; reverse unwinds L/u/v -> u′v′ -> XYZ -> linear sRGB.
  private buildLuvSteps( r: number, g: number, b: number, xyz: number[], luv: [ number, number, number ] ): { label: string; expr: string }[] {
    const f4 = ( n: number ): string => ( Math.round( n * 10000 ) / 10000 ).toString();
    const un = 0.1978, vn = 0.4683; // D65 white on the u′v′ diagram (4 decimals, for the copy)
    if ( !this.isReverse ) {
      const d = xyz[ 0 ] + 15 * xyz[ 1 ] + 3 * xyz[ 2 ];
      const up = d > 0 ? ( 4 * xyz[ 0 ] ) / d : un;
      const vp = d > 0 ? ( 9 * xyz[ 1 ] ) / d : vn;
      return [
        { label: 'To XYZ', expr: `linearize the channels and mix into CIE XYZ (D65) → X = ${ xyz[ 0 ] }, Y = ${ xyz[ 1 ] }, Z = ${ xyz[ 2 ] }` },
        { label: 'Lightness', expr: `L = 116·∛(Y ÷ 100) − 16 = ${ luv[ 0 ] } (the same lightness as CIELAB)` },
        { label: 'Chromaticity', expr: `u′ = 4X ÷ (X + 15Y + 3Z) = ${ f4( up ) }, v′ = 9Y ÷ (X + 15Y + 3Z) = ${ f4( vp ) }` },
        { label: 'u and v', expr: `u = 13L·(u′ − ${ un }) = ${ luv[ 1 ] }, v = 13L·(v′ − ${ vn }) = ${ luv[ 2 ] } (the D65 white sits at u′ = ${ un }, v′ = ${ vn })` },
      ];
    }
    const up = luv[ 0 ] > 0 ? luv[ 1 ] / ( 13 * luv[ 0 ] ) + un : un;
    const vp = luv[ 0 ] > 0 ? luv[ 2 ] / ( 13 * luv[ 0 ] ) + vn : vn;
    return [
      { label: 'Chromaticity', expr: `u′ = u ÷ 13L + ${ un } = ${ f4( up ) }, v′ = v ÷ 13L + ${ vn } = ${ f4( vp ) }` },
      { label: 'To XYZ', expr: `Y = ((L + 16) ÷ 116)³ × 100 = ${ xyz[ 1 ] }, X = Y·9u′ ÷ 4v′ = ${ xyz[ 0 ] }, Z = Y·(12 − 3u′ − 20v′) ÷ 4v′ = ${ xyz[ 2 ] }` },
      { label: 'Un-mix', expr: 'multiply by the XYZ → sRGB matrix (D65) and clamp to the sRGB gamut' },
      { label: 'Encode', expr: 'reapply the sRGB curve' },
      { label: 'Channels', expr: `scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  // Worked Display P3 derivation: sRGB and P3 share the transfer curve and
  // differ only in primaries, so the whole trip is decode -> 3x3 matrix -> encode.
  private buildP3Steps( r: number, g: number, b: number, p3: [ number, number, number ] ): { label: string; expr: string }[] {
    if ( !this.isReverse ) {
      return [
        { label: 'Linearize', expr: `decode the sRGB curve on R = ${ r }, G = ${ g }, B = ${ b } (each ÷ 255) into linear light` },
        { label: 'Re-mix', expr: 'multiply by the sRGB → Display P3 matrix (P3 has wider red and green primaries, so the numbers shrink toward the middle)' },
        { label: 'Encode', expr: `reapply the shared transfer curve → r = ${ p3[ 0 ] }, g = ${ p3[ 1 ] }, b = ${ p3[ 2 ] }` },
      ];
    }
    return [
      { label: 'Linearize', expr: `decode the transfer curve on r = ${ p3[ 0 ] }, g = ${ p3[ 1 ] }, b = ${ p3[ 2 ] } into linear P3 light` },
      { label: 'Re-mix', expr: 'multiply by the Display P3 → sRGB matrix and clamp to the smaller sRGB gamut' },
      { label: 'Encode', expr: 'reapply the sRGB curve' },
      { label: 'Channels', expr: `scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  // Worked CIE XYZ derivation: the first half of every Lab-style conversion.
  private buildXyzSteps( r: number, g: number, b: number, xyz: number[] ): { label: string; expr: string }[] {
    if ( !this.isReverse ) {
      return [
        { label: 'Linearize', expr: `decode the sRGB curve on R = ${ r }, G = ${ g }, B = ${ b } (each ÷ 255) into linear light` },
        { label: 'Mix', expr: 'weight the three channels by the sRGB → XYZ matrix (D65): red, green and blue each contribute to every tristimulus value' },
        { label: 'Scale', expr: `× 100 so white sits at Y = 100 → X = ${ xyz[ 0 ] }, Y = ${ xyz[ 1 ] }, Z = ${ xyz[ 2 ] }` },
      ];
    }
    return [
      { label: 'Scale', expr: `X = ${ xyz[ 0 ] }, Y = ${ xyz[ 1 ] }, Z = ${ xyz[ 2 ] } each ÷ 100` },
      { label: 'Un-mix', expr: 'multiply by the XYZ → sRGB matrix (D65), clamp anything outside the sRGB gamut' },
      { label: 'Encode', expr: 'reapply the sRGB curve' },
      { label: 'Channels', expr: `scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  // Worked Yxy derivation: XYZ split into luminance and the chromaticity pair.
  private buildYxySteps( r: number, g: number, b: number, xyz: number[], yxy: [ number, number, number ] ): { label: string; expr: string }[] {
    if ( !this.isReverse ) {
      return [
        { label: 'To XYZ', expr: `linearize the channels and mix into CIE XYZ (D65) → X = ${ xyz[ 0 ] }, Y = ${ xyz[ 1 ] }, Z = ${ xyz[ 2 ] }` },
        { label: 'Luminance', expr: `Y stays as it is: ${ yxy[ 0 ] }` },
        { label: 'Chromaticity', expr: `x = X ÷ (X + Y + Z) = ${ yxy[ 1 ] }, y = Y ÷ (X + Y + Z) = ${ yxy[ 2 ] }` },
      ];
    }
    return [
      { label: 'To XYZ', expr: `X = x·Y ÷ y = ${ xyz[ 0 ] }, Y = ${ xyz[ 1 ] }, Z = (1 − x − y)·Y ÷ y = ${ xyz[ 2 ] }` },
      { label: 'Un-mix', expr: 'multiply by the XYZ → sRGB matrix (D65) and clamp to the sRGB gamut' },
      { label: 'Encode', expr: 'reapply the sRGB curve' },
      { label: 'Channels', expr: `scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  // Worked Hunter Lab derivation (1958 formulation on D65 XYZ, 0..100 scale).
  private buildHunterSteps( r: number, g: number, b: number, xyz: number[], hl: [ number, number, number ] ): { label: string; expr: string }[] {
    if ( !this.isReverse ) {
      return [
        { label: 'To XYZ', expr: `linearize the channels and mix into CIE XYZ (D65) → X = ${ xyz[ 0 ] }, Y = ${ xyz[ 1 ] }, Z = ${ xyz[ 2 ] }` },
        { label: 'Lightness', expr: `L = 10·√Y = ${ hl[ 0 ] }` },
        { label: 'Axes', expr: `a = 17.5·(1.02·X − Y) ÷ √Y = ${ hl[ 1 ] }, b = 7·(Y − 0.847·Z) ÷ √Y = ${ hl[ 2 ] }` },
      ];
    }
    return [
      { label: 'To XYZ', expr: `Y = (L ÷ 10)² = ${ xyz[ 1 ] }, X = (a·√Y ÷ 17.5 + Y) ÷ 1.02 = ${ xyz[ 0 ] }, Z = (Y − b·√Y ÷ 7) ÷ 0.847 = ${ xyz[ 2 ] }` },
      { label: 'Un-mix', expr: 'multiply by the XYZ → sRGB matrix (D65) and clamp to the sRGB gamut' },
      { label: 'Encode', expr: 'reapply the sRGB curve' },
      { label: 'Channels', expr: `scale by 255 → R = ${ r }, G = ${ g }, B = ${ b }` },
    ];
  }

  /** Opening token of the split notation: most families are `family(`, the
   *  CSS colour spaces spell it differently. */
  get notationPrefix(): string {
    const f = this.def.family;
    return f === 'p3' ? 'color(display-p3 ' : f === 'hunter' ? 'hunter-lab(' : `${ f }(`;
  }

  // The example table's from/to strings for the current direction + family.
  exampleFrom( ex: ConverterExample ): string {
    if ( !this.isReverse ) {
      return ex.hex;
    }
    return this.def.family === 'hsl' ? ex.hsl
      : this.def.family === 'hsv' ? ex.hsv
        : this.def.family === 'hsb' ? ex.hsv.replace( 'hsv(', 'hsb(' )
          : this.def.family === 'hwb' ? ex.hwb
            : this.def.family === 'cmyk' ? ex.cmyk : this.def.family === 'oklch' ? ex.oklch : this.def.family === 'oklab' ? ex.oklab : this.def.family === 'lab' ? ex.cielab : this.def.family === 'lch' ? ex.cielch : this.def.family === 'luv' ? ex.cieluv : this.def.family === 'p3' ? ex.p3 : this.def.family === 'xyz' ? ex.xyz : this.def.family === 'yxy' ? ex.yxy : this.def.family === 'hunter' ? ex.hunter : ex.rgb;
  }

  exampleTo( ex: ConverterExample ): string {
    if ( this.isReverse ) {
      return ex.hex;
    }
    return this.def.family === 'hsl' ? ex.hsl
      : this.def.family === 'hsv' ? ex.hsv
        : this.def.family === 'hsb' ? ex.hsv.replace( 'hsv(', 'hsb(' )
          : this.def.family === 'hwb' ? ex.hwb
            : this.def.family === 'cmyk' ? ex.cmyk : this.def.family === 'oklch' ? ex.oklch : this.def.family === 'oklab' ? ex.oklab : this.def.family === 'lab' ? ex.cielab : this.def.family === 'lch' ? ex.cielch : this.def.family === 'luv' ? ex.cieluv : this.def.family === 'p3' ? ex.p3 : this.def.family === 'xyz' ? ex.xyz : this.def.family === 'yxy' ? ex.yxy : this.def.family === 'hunter' ? ex.hunter : ex.rgb;
  }

  // Smooth-scroll to a section, clearing the fixed header (and, on mobile, the
  // sticky section bar). Mirrors the color tool: this app scrolls on <body>, so
  // tween body.scrollTop by hand (body.scrollTo smooth is a no-op here).
  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 )
      ? document.body
      : document.documentElement;

    // The colour pill jumps to the very top (the swatch + hex hero) and becomes
    // the selected entry.
    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.scrollActivePillToStart();
      this.tweenScroll( scroller, 0 );
      return;
    }

    const el = document.getElementById( 'sec-' + key );
    if ( !el ) {
      return;
    }
    this.activeSection = key;
    this.scrollActivePillToStart();

    // clear the nav and the sticky chips' bar under it (owner 2026-10-12)
    let headerOffset = 84;
    const bar = document.querySelector( '.cp-chipbar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    }
    const target = Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset );
    this.tweenScroll( scroller, target );
  }

  // Mobile only: keep the active section pill centred in the horizontal strip,
  // so the current section + its neighbours stay visible. No-op on the desktop
  // (vertical) sidebar.
  private scrollActivePillToStart(): void {
    setTimeout( () => {
      const strip = document.querySelector( '.cp-chipbar-row' ) as HTMLElement | null;
      if ( !strip || strip.scrollWidth <= strip.clientWidth ) {
        return;
      }
      const active = strip.querySelector( '.pl-chip.on' ) as HTMLElement | null;
      if ( !active ) {
        return;
      }
      // Centre the active pill by scrolling ONLY the strip horizontally (absolute,
      // so repeated calls don't accumulate). Avoids scrollIntoView, which on iOS
      // also nudges the page's vertical scroll, read as the page "snapping".
      const sr = strip.getBoundingClientRect();
      const ar = active.getBoundingClientRect();
      const target = strip.scrollLeft + ( ar.left - sr.left ) - ( sr.width - ar.width ) / 2;
      strip.scrollTo( { left: Math.max( 0, target ), behavior: 'smooth' } );
    } );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const duration = 420;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / duration );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) {
        requestAnimationFrame( step );
      }
    };
    requestAnimationFrame( step );
  }

  copy( text: string, key: string ): void {
    this.clipboard.copy( text );
    this.copiedKey = key;
    if ( this.copiedTimer ) { clearTimeout( this.copiedTimer ); }
    this.copiedTimer = setTimeout( () => ( this.copiedKey = '' ), 1200 );
  }

  private refresh(): void {
    this.buildHeroBackground();
    this.buildRelatedThemes();
    this.buildFormats();
  }

  // The current color written every common RGB way + the hex->decimal breakdown
  // that the "how it works" section explains, both recomputed on every change.
  private buildFormats(): void {
    const rgb = this.colorService.hexToRgb( this.hex );
    const r = rgb.items[ 0 ]?.value ?? 0;
    const g = rgb.items[ 1 ]?.value ?? 0;
    const b = rgb.items[ 2 ]?.value ?? 0;
    const pct = ( n: number ): number => Math.round( ( n / 255 ) * 100 );
    const dec = ( n: number ): string => ( n / 255 ).toFixed( 3 );
    this.rgbString = `rgb(${ r }, ${ g }, ${ b })`;
    const hslVals = this.colorService.hexToHslInts( this.hex );
    const hsvArr = this.colorService.hexToHsv( this.hex );
    const hsvVals = { h: hsvArr[ 0 ], s: hsvArr[ 1 ], v: hsvArr[ 2 ] };
    const hwbArr = this.colorService.hexToHwb( this.hex );
    const hwbVals = { h: hwbArr[ 0 ], w: hwbArr[ 1 ], b: hwbArr[ 2 ] };
    const cmykVals = this.colorService.hexToCmyk( this.hex ).items.map( ( i: { value: number } ) => i.value );
    const oklchVals = this.colorService.hexToOklch( this.hex );
    const oklabVals = this.colorService.hexToOklab( this.hex );
    const labVals = this.colorService.hexToLab( this.hex );
    const lchVals = this.colorService.hexToLch( this.hex );
    const luvVals = this.colorService.hexToLuv( this.hex );
    const p3Vals = this.colorService.hexToDisplayP3( this.hex );
    const xyzVals = this.colorService.hexToXyz( this.hex ).map( ( n: number ) => Math.round( n * 10000 ) / 100 );
    const yxyVals = this.colorService.hexToYxy( this.hex );
    const hunterVals = this.colorService.hexToHunterLab( this.hex );
    if ( this.def.family === 'oklch' ) {
      const lPct = Math.round( oklchVals[ 0 ] * 100 );
      const cVal = Math.round( oklchVals[ 1 ] * 1000 ) / 1000;
      const hVal = Math.round( oklchVals[ 2 ] );
      // oklch() is CSS Color 4 syntax: space-separated, L written as a percentage.
      this.otherString = `oklch(${ lPct }% ${ cVal } ${ hVal })`;
      this.otherParts = [
        { display: lPct + '%', cap: 'Lightness', tint: '#3f7fc4' },
        { display: String( cVal ), cap: 'Chroma', tint: '#3d9f6b' },
        { display: String( hVal ), cap: 'Hue', tint: '#b0641f' },
      ];
      this.otherSteps = this.buildOklchSteps( r, g, b, { l: oklchVals[ 0 ], c: oklchVals[ 1 ], h: oklchVals[ 2 ] } );
    } else if ( this.def.family === 'oklab' ) {
      const lPct = Math.round( oklabVals[ 0 ] * 100 );
      const aVal = Math.round( oklabVals[ 1 ] * 1000 ) / 1000;
      const bVal = Math.round( oklabVals[ 2 ] * 1000 ) / 1000;
      // oklab() is CSS Color 4 syntax: space-separated, L a percentage, a/b signed.
      this.otherString = `oklab(${ lPct }% ${ aVal } ${ bVal })`;
      this.otherParts = [
        { display: lPct + '%', cap: 'Lightness', tint: '#3f7fc4' },
        { display: String( aVal ), cap: 'a', tint: '#c2410c' },
        { display: String( bVal ), cap: 'b', tint: '#7c3aed' },
      ];
      this.otherSteps = this.buildOklabSteps( r, g, b, { l: oklabVals[ 0 ], a: oklabVals[ 1 ], b: oklabVals[ 2 ] } );
    } else if ( this.def.family === 'lab' ) {
      const lPct = Math.round( labVals[ 0 ] );
      const aVal = Math.round( labVals[ 1 ] * 100 ) / 100;
      const bVal = Math.round( labVals[ 2 ] * 100 ) / 100;
      // CSS lab(): space-separated, L a percentage, a/b signed (~±128).
      this.otherString = `lab(${ lPct }% ${ aVal } ${ bVal })`;
      this.otherParts = [
        { display: lPct + '%', cap: 'Lightness', tint: '#3f7fc4' },
        { display: String( aVal ), cap: 'a', tint: '#c2410c' },
        { display: String( bVal ), cap: 'b', tint: '#7c3aed' },
      ];
      this.otherSteps = this.buildLabSteps( r, g, b, { l: labVals[ 0 ], a: labVals[ 1 ], b: labVals[ 2 ] } );
    } else if ( this.def.family === 'lch' ) {
      const lPct = Math.round( lchVals[ 0 ] );
      const cVal = Math.round( lchVals[ 1 ] * 100 ) / 100;
      const hVal = Math.round( lchVals[ 2 ] );
      // CSS lch(): space-separated, L a percentage, chroma 0..~150, hue in degrees.
      this.otherString = `lch(${ lPct }% ${ cVal } ${ hVal })`;
      this.otherParts = [
        { display: lPct + '%', cap: 'Lightness', tint: '#3f7fc4' },
        { display: String( cVal ), cap: 'Chroma', tint: '#3d9f6b' },
        { display: String( hVal ), cap: 'Hue', tint: '#b0641f' },
      ];
      this.otherSteps = this.buildLchSteps( r, g, b, { l: lchVals[ 0 ], c: lchVals[ 1 ], h: lchVals[ 2 ] } );
    } else if ( this.def.family === 'luv' ) {
      // CIELUV has no CSS function: comma-separated luv(L, u, v), u/v signed.
      this.otherString = `luv(${ luvVals[ 0 ] }, ${ luvVals[ 1 ] }, ${ luvVals[ 2 ] })`;
      this.otherParts = [
        { display: String( luvVals[ 0 ] ), cap: 'L', tint: '#3f7fc4' },
        { display: String( luvVals[ 1 ] ), cap: 'u', tint: '#c2410c' },
        { display: String( luvVals[ 2 ] ), cap: 'v', tint: '#7c3aed' },
      ];
      this.otherSteps = this.buildLuvSteps( r, g, b, xyzVals, luvVals );
    } else if ( this.def.family === 'p3' ) {
      // CSS color(display-p3 r g b): space-separated 0..1 channels.
      this.otherString = `color(display-p3 ${ p3Vals[ 0 ] } ${ p3Vals[ 1 ] } ${ p3Vals[ 2 ] })`;
      this.otherParts = [
        { display: String( p3Vals[ 0 ] ), cap: 'Red', tint: '#e53935' },
        { display: String( p3Vals[ 1 ] ), cap: 'Green', tint: '#43a047' },
        { display: String( p3Vals[ 2 ] ), cap: 'Blue', tint: '#1e88e5' },
      ];
      this.otherSteps = this.buildP3Steps( r, g, b, p3Vals );
    } else if ( this.def.family === 'xyz' ) {
      // CIE XYZ on the 0..100 scale (white at Y = 100), comma-separated.
      this.otherString = `xyz(${ xyzVals[ 0 ] }, ${ xyzVals[ 1 ] }, ${ xyzVals[ 2 ] })`;
      this.otherParts = [
        { display: String( xyzVals[ 0 ] ), cap: 'X', tint: '#c2185b' },
        { display: String( xyzVals[ 1 ] ), cap: 'Y', tint: '#3d9f6b' },
        { display: String( xyzVals[ 2 ] ), cap: 'Z', tint: '#3f7fc4' },
      ];
      this.otherSteps = this.buildXyzSteps( r, g, b, xyzVals );
    } else if ( this.def.family === 'yxy' ) {
      // Yxy: luminance Y (0..100) then the chromaticity coordinates x, y.
      this.otherString = `yxy(${ yxyVals[ 0 ] }, ${ yxyVals[ 1 ] }, ${ yxyVals[ 2 ] })`;
      this.otherParts = [
        { display: String( yxyVals[ 0 ] ), cap: 'Y', tint: '#3d9f6b' },
        { display: String( yxyVals[ 1 ] ), cap: 'x', tint: '#c2185b' },
        { display: String( yxyVals[ 2 ] ), cap: 'y', tint: '#3f7fc4' },
      ];
      this.otherSteps = this.buildYxySteps( r, g, b, xyzVals, yxyVals );
    } else if ( this.def.family === 'hunter' ) {
      this.otherString = `hunter-lab(${ hunterVals[ 0 ] }, ${ hunterVals[ 1 ] }, ${ hunterVals[ 2 ] })`;
      this.otherParts = [
        { display: String( hunterVals[ 0 ] ), cap: 'L', tint: '#3f7fc4' },
        { display: String( hunterVals[ 1 ] ), cap: 'a', tint: '#3d9f6b' },
        { display: String( hunterVals[ 2 ] ), cap: 'b', tint: '#7c3aed' },
      ];
      this.otherSteps = this.buildHunterSteps( r, g, b, xyzVals, hunterVals );
    } else if ( this.def.family === 'cmyk' ) {
      this.otherString = `cmyk(${ cmykVals[ 0 ] }%, ${ cmykVals[ 1 ] }%, ${ cmykVals[ 2 ] }%, ${ cmykVals[ 3 ] }%)`;
      // The four parts wear the classic process ink tints.
      this.otherParts = [
        { display: cmykVals[ 0 ] + '%', cap: 'Cyan', tint: '#0b9fd8' },
        { display: cmykVals[ 1 ] + '%', cap: 'Magenta', tint: '#d1197e' },
        { display: cmykVals[ 2 ] + '%', cap: 'Yellow', tint: '#e0b400' },
        { display: cmykVals[ 3 ] + '%', cap: 'Key', tint: '#3d3d3d' },
      ];
      this.otherSteps = this.buildCmykSteps( r, g, b, cmykVals );
    } else if ( this.def.family === 'hwb' ) {
      // hwb() is CSS Color 4 syntax: space-separated, no comma form exists.
      this.otherString = `hwb(${ hwbVals.h } ${ hwbVals.w }% ${ hwbVals.b }%)`;
      this.otherParts = [
        { display: String( hwbVals.h ), cap: 'Hue', tint: '#b0641f' },
        { display: hwbVals.w + '%', cap: 'Whiteness', tint: '#3d9f6b' },
        { display: hwbVals.b + '%', cap: 'Blackness', tint: '#3f7fc4' },
      ];
      this.otherSteps = this.buildHwbSteps( r, g, b, hwbVals );
    } else if ( this.def.family === 'hsv' || this.def.family === 'hsb' ) {
      this.otherString = `${ this.def.family }(${ hsvVals.h }, ${ hsvVals.s }%, ${ hsvVals.v }%)`;
      this.otherParts = [
        { display: String( hsvVals.h ), cap: 'Hue', tint: '#b0641f' },
        { display: hsvVals.s + '%', cap: 'Saturation', tint: '#3d9f6b' },
        { display: hsvVals.v + '%', cap: this.def.family === 'hsb' ? 'Brightness' : 'Value', tint: '#3f7fc4' },
      ];
      this.otherSteps = this.buildHsvSteps( r, g, b, hsvVals );
    } else if ( this.def.family === 'hsl' ) {
      this.otherString = `hsl(${ hslVals.h }, ${ hslVals.s }%, ${ hslVals.l }%)`;
      this.otherParts = [
        { display: String( hslVals.h ), cap: 'Hue', tint: '#b0641f' },
        { display: hslVals.s + '%', cap: 'Saturation', tint: '#3d9f6b' },
        { display: hslVals.l + '%', cap: 'Lightness', tint: '#3f7fc4' },
      ];
      this.otherSteps = this.buildHslSteps( r, g, b, hslVals );
    } else {
      // RGB family (hex<->rgb): the companion value is the color's rgb(), not
      // hsl — with a hex target rgb is the meaningful pairing (matches refreshSeo).
      this.otherString = this.rgbString;
      this.otherParts = [
        { display: String( r ), cap: 'Red', tint: '#d64f4a' },
        { display: String( g ), cap: 'Green', tint: '#3d9f6b' },
        { display: String( b ), cap: 'Blue', tint: '#3f7fc4' },
      ];
      this.otherSteps = [];
    }
    const hu = this.hex.replace( '#', '' ).toUpperCase();
    if ( this.isReverse ) {
      this.rgbFormats = [
        { label: 'HEX', value: `#${ hu }`, key: 'f-hex' },
        { label: 'HEX With Alpha', value: `#${ hu }FF`, key: 'f-alpha' },
      ];
    } else if ( this.def.family === 'hsl' ) {
      this.rgbFormats = [
        { label: 'HSL', value: this.otherString, key: 'f-hsl' },
        { label: 'HSLA', value: `hsla(${ hslVals.h }, ${ hslVals.s }%, ${ hslVals.l }%, 1)`, key: 'f-hsla' },
      ];
    } else if ( this.def.family === 'hsv' ) {
      this.rgbFormats = [
        { label: 'HSV', value: this.otherString, key: 'f-hsv' },
        { label: 'HSVA', value: `hsva(${ hsvVals.h }, ${ hsvVals.s }%, ${ hsvVals.v }%, 1)`, key: 'f-hsva' },
      ];
    } else if ( this.def.family === 'hsb' ) {
      this.rgbFormats = [
        { label: 'HSB', value: this.otherString, key: 'f-hsb' },
        { label: 'HSBA', value: `hsba(${ hsvVals.h }, ${ hsvVals.s }%, ${ hsvVals.v }%, 1)`, key: 'f-hsba' },
      ];
    } else if ( this.def.family === 'hwb' ) {
      this.rgbFormats = [
        { label: 'HWB', value: this.otherString, key: 'f-hwb' },
        { label: 'HWB With Alpha', value: `hwb(${ hwbVals.h } ${ hwbVals.w }% ${ hwbVals.b }% / 1)`, key: 'f-hwba' },
      ];
    } else if ( this.def.family === 'cmyk' ) {
      this.rgbFormats = [
        { label: 'CMYK', value: this.otherString, key: 'f-cmyk' },
        { label: 'CMYK Normalized', value: cmykVals.map( ( v: number ) => ( v / 100 ).toFixed( 2 ) ).join( ', ' ), key: 'f-cmykn' },
      ];
    } else if ( this.def.family === 'oklch' ) {
      const lPct = Math.round( oklchVals[ 0 ] * 100 );
      const cVal = Math.round( oklchVals[ 1 ] * 1000 ) / 1000;
      const hVal = Math.round( oklchVals[ 2 ] );
      this.rgbFormats = [
        { label: 'OKLCH', value: this.otherString, key: 'f-oklch' },
        { label: 'OKLCH With Alpha', value: `oklch(${ lPct }% ${ cVal } ${ hVal } / 1)`, key: 'f-oklcha' },
      ];
    } else if ( this.def.family === 'oklab' ) {
      const lPct = Math.round( oklabVals[ 0 ] * 100 );
      const aVal = Math.round( oklabVals[ 1 ] * 1000 ) / 1000;
      const bVal = Math.round( oklabVals[ 2 ] * 1000 ) / 1000;
      this.rgbFormats = [
        { label: 'OKLAB', value: this.otherString, key: 'f-oklab' },
        { label: 'OKLAB With Alpha', value: `oklab(${ lPct }% ${ aVal } ${ bVal } / 1)`, key: 'f-oklaba' },
      ];
    } else if ( this.def.family === 'lab' ) {
      const lPct = Math.round( labVals[ 0 ] );
      const aVal = Math.round( labVals[ 1 ] * 100 ) / 100;
      const bVal = Math.round( labVals[ 2 ] * 100 ) / 100;
      this.rgbFormats = [
        { label: 'CIELAB', value: this.otherString, key: 'f-lab' },
        { label: 'CIELAB With Alpha', value: `lab(${ lPct }% ${ aVal } ${ bVal } / 1)`, key: 'f-laba' },
      ];
    } else if ( this.def.family === 'lch' ) {
      const lPct = Math.round( lchVals[ 0 ] );
      const cVal = Math.round( lchVals[ 1 ] * 100 ) / 100;
      const hVal = Math.round( lchVals[ 2 ] );
      this.rgbFormats = [
        { label: 'CIELCH', value: this.otherString, key: 'f-lch' },
        { label: 'CIELCH With Alpha', value: `lch(${ lPct }% ${ cVal } ${ hVal } / 1)`, key: 'f-lcha' },
      ];
    } else if ( this.def.family === 'luv' ) {
      this.rgbFormats = [
        { label: 'CIELUV', value: this.otherString, key: 'f-luv' },
        { label: 'CIELUV Values', value: `L* ${ luvVals[ 0 ] }, u* ${ luvVals[ 1 ] }, v* ${ luvVals[ 2 ] }`, key: 'f-luvv' },
      ];
    } else if ( this.def.family === 'p3' ) {
      this.rgbFormats = [
        { label: 'Display P3', value: this.otherString, key: 'f-p3' },
        { label: 'Display P3 Percent', value: `color(display-p3 ${ Math.round( p3Vals[ 0 ] * 100 ) }% ${ Math.round( p3Vals[ 1 ] * 100 ) }% ${ Math.round( p3Vals[ 2 ] * 100 ) }%)`, key: 'f-p3p' },
        { label: 'Display P3 With Alpha', value: `color(display-p3 ${ p3Vals[ 0 ] } ${ p3Vals[ 1 ] } ${ p3Vals[ 2 ] } / 1)`, key: 'f-p3a' },
      ];
    } else if ( this.def.family === 'xyz' ) {
      this.rgbFormats = [
        { label: 'XYZ', value: this.otherString, key: 'f-xyz' },
        { label: 'CSS xyz-d65', value: `color(xyz-d65 ${ Math.round( xyzVals[ 0 ] * 100 ) / 10000 } ${ Math.round( xyzVals[ 1 ] * 100 ) / 10000 } ${ Math.round( xyzVals[ 2 ] * 100 ) / 10000 })`, key: 'f-xyzcss' },
      ];
    } else if ( this.def.family === 'yxy' ) {
      this.rgbFormats = [
        { label: 'Yxy', value: this.otherString, key: 'f-yxy' },
        { label: 'xyY', value: `xyY(${ yxyVals[ 1 ] }, ${ yxyVals[ 2 ] }, ${ yxyVals[ 0 ] })`, key: 'f-xyy' },
      ];
    } else if ( this.def.family === 'hunter' ) {
      this.rgbFormats = [
        { label: 'Hunter Lab', value: this.otherString, key: 'f-hunter' },
        { label: 'Hunter Lab Values', value: `${ hunterVals[ 0 ] }, ${ hunterVals[ 1 ] }, ${ hunterVals[ 2 ] }`, key: 'f-hunterv' },
      ];
    } else {
      this.rgbFormats = [
        { label: 'RGB', value: `rgb(${ r }, ${ g }, ${ b })`, key: 'f-rgb' },
        { label: 'RGB Percent', value: `rgb(${ pct( r ) }%, ${ pct( g ) }%, ${ pct( b ) }%)`, key: 'f-pct' },
        { label: 'RGB Normalized', value: `${ dec( r ) }, ${ dec( g ) }, ${ dec( b ) }`, key: 'f-dec' },
        { label: 'RGBA', value: `rgba(${ r }, ${ g }, ${ b }, 1)`, key: 'f-rgba' },
      ];
    }
    const h = hu;
    this.outHex = '#' + h;
    const chan = ( pair: string, dec: number, tint: string, channel: string ) => ( {
      channel, hexPair: pair, hi: parseInt( pair[ 0 ], 16 ), lo: parseInt( pair[ 1 ], 16 ), dec, tint,
    } );
    this.workedChannels = [
      chan( h.substring( 0, 2 ), r, '#d64f4a', 'Red' ),
      chan( h.substring( 2, 4 ), g, '#3d9f6b', 'Green' ),
      chan( h.substring( 4, 6 ), b, '#3f7fc4', 'Blue' ),
    ];
  }

  // Same color-derived banded backdrop as the color tool's hero.
  private buildHeroBackground(): void {
    const colors = this.colorService.generateMonochromeColors( this.hex, 11 )
      .map( ( c: string ) => ( c.startsWith( '#' ) ? c : '#' + c ) )
      .filter( ( c: string ) => /^#[0-9a-fA-F]{6}$/.test( c ) );
    if ( !colors.length ) { this.heroBackground = ''; return; }
    const angle = 115;
    const alpha = '99'; // 0.6 * 255
    const ramp = colors.concat( colors.slice( 1, -1 ).reverse() );
    const band = 100 / ramp.length;
    const stops: string[] = [];
    ramp.forEach( ( color, i ) => {
      stops.push( `${ color }${ alpha } ${ i * band }%`, `${ color }${ alpha } ${ ( i + 1 ) * band }%` );
    } );
    this.heroBackground = `linear-gradient(${ angle }deg, ${ stops.join( ', ' ) }), var(--background-color, #f4f6f9)`;
  }

  // IDE themes whose palette sits closest to the current color. Ported verbatim
  // from the color tool so the Related Themes section is identical.
  private buildRelatedThemes(): void {
    const rh = this.hex.replace( '#', '' );
    const cr = parseInt( rh.substring( 0, 2 ), 16 );
    const cg = parseInt( rh.substring( 2, 4 ), 16 );
    const cb = parseInt( rh.substring( 4, 6 ), 16 );
    const spread = ( hexv: string ): number => {
      const h = hexv.replace( '#', '' );
      const pr = parseInt( h.substring( 0, 2 ), 16 );
      const pg = parseInt( h.substring( 2, 4 ), 16 );
      const pb = parseInt( h.substring( 4, 6 ), 16 );
      return Math.max( pr, pg, pb ) - Math.min( pr, pg, pb );
    };
    if ( Math.max( cr, cg, cb ) - Math.min( cr, cg, cb ) <= 6 ) {
      const greyScored: { product: Product; category: string; d: number }[] = [];
      for ( const line of THEMES ) {
        for ( const theme of line.products ) {
          const palette: string[] = ( theme.palette || [] ).filter(
            ( p: string ) => typeof p === 'string' && /^#?[0-9a-fA-F]{6}$/.test( p.replace( '#', '' ) )
          );
          if ( palette.length === 0 ) { continue; }
          const maxSpread = Math.max( ...palette.slice( 0, 6 ).map( spread ) );
          greyScored.push( { product: theme, category: line.name, d: maxSpread } );
        }
      }
      greyScored.sort( ( a, b ) => a.d - b.d );
      const NEUTRAL_MAX = 45;
      let kept = greyScored.filter( ( s ) => s.d <= NEUTRAL_MAX );
      if ( kept.length === 0 ) { kept = greyScored.slice( 0, 1 ); }
      this.relatedThemeList = kept.slice( 0, 3 ).map( ( { d, ...rest } ) => rest );
      return;
    }
    const targetL = this.colorService.hexToLab( this.hex )[ 0 ];
    const targetDark = targetL < 50;
    const appStrength = Math.min( 1, Math.abs( targetL - 50 ) / 35 );
    const th = this.colorService.hexToHsl( this.hex.replace( '#', '' ) );
    const targetGrey = th.s < 12;
    const colorDist = ( hex: string ): number => {
      const c = this.colorService.hexToHsl( hex.replace( '#', '' ) );
      let dh = Math.abs( c.h - th.h );
      if ( dh > 180 ) { dh = 360 - dh; }
      const satW = Math.min( c.s, th.s ) / 100;
      const hueTerm = dh * satW;
      const ds = c.s - th.s;
      const dl = c.l - th.l;
      return Math.sqrt( hueTerm * hueTerm + 0.12 * ds * ds + 0.10 * dl * dl );
    };
    const scored: { product: Product; category: string; d: number }[] = [];
    for ( const line of THEMES ) {
      for ( const theme of line.products ) {
        const palette: string[] = ( theme.palette || [] ).filter(
          ( p: string ) => typeof p === 'string' && /^#?[0-9a-fA-F]{6}$/.test( p.replace( '#', '' ) )
        );
        if ( palette.length === 0 ) { continue; }
        let best = Infinity;
        const LEAD_WEIGHTS = [ 0.8, 0.92, 1.05, 1.15, 1.25, 1.35 ];
        const lead = palette.slice( 0, LEAD_WEIGHTS.length );
        lead.forEach( ( p, i ) => {
          const d = colorDist( p ) * LEAD_WEIGHTS[ i ];
          if ( d < best ) { best = d; }
        } );
        if ( targetGrey ) {
          const leadHsl = lead.map( ( p ) => this.colorService.hexToHsl( p.replace( '#', '' ) ) );
          const ident = leadHsl.reduce( ( a, b ) => ( b.s > a.s ? b : a ) );
          let dh = Math.abs( ident.h - th.h );
          if ( dh > 180 ) { dh = 360 - dh; }
          best += dh * ( ident.s / 100 ) * 0.05;
        }
        const appearance = ( theme.appearance || '' ).toLowerCase();
        const themeLight = appearance.includes( 'light' ) && !appearance.includes( 'dark' );
        const themeDark = appearance.includes( 'dark' ) && !appearance.includes( 'light' );
        const matches = ( targetDark && themeDark ) || ( !targetDark && themeLight );
        const mismatched = ( targetDark && themeLight ) || ( !targetDark && themeDark );
        const score = best * ( matches ? 1 - 0.3 * appStrength : mismatched ? 1 + 0.5 * appStrength : 1 );
        scored.push( { product: theme, category: line.name, d: score } );
      }
    }
    scored.sort( ( a, b ) => a.d - b.d );
    const best = scored.length ? scored[ 0 ].d : 0;
    const DELTA = 6;
    const ABS = 22;
    const MAX = 3;
    const MIN = 1;
    const good = scored.filter( s => s.d <= best + DELTA && s.d <= ABS ).length;
    const count = Math.min( MAX, Math.max( MIN, good ) );
    this.relatedThemeList = scored.slice( 0, count ).map( ( { d, ...rest } ) => rest );
  }
}
